import { useState, useCallback, useEffect, useRef, useMemo, Fragment } from 'react'
import CodeMirror from './common/CodeMirrorWithWrap'
import { sql } from '@codemirror/lang-sql'
import { yaml } from '@codemirror/lang-yaml'
import { search, searchKeymap, selectSelectionMatches } from '@codemirror/search'
import { keymap, EditorView } from '@codemirror/view'
import { Prec } from '@codemirror/state'
import { api } from '../api'
import { APP_DEFAULTS } from '../defaults'
import SearchInput from './common/SearchInput'
import HighlightText from './common/HighlightText'
import { filterByText } from '../utils/filterByText'
import { downloadText, ensureExtension } from '../utils/download'
import { suggestDuplicateName } from '../utils/duplicateName'
import DuplicateDialog from './common/DuplicateDialog'
import GcsExportDialog from './common/GcsExportDialog'
import GcsImportDialog from './common/GcsImportDialog'
import SvRecommendDialog from './common/SvRecommendDialog'
import TokenBadge from './common/TokenBadge'
import { extractViewSummariesFromYaml } from '../utils/yamlExtract'
import CopyButton from './common/CopyButton'
import { useRunningJobs } from '../hooks/useRunningJobs'
import './SqlConverterWorkbench.css'

/**
 * SQL Workbench — 단계별 SQL 정제 → 최종 YAML 변환 (KBNL-63)
 *
 * 한 워크벤치는:
 *   Step 0  : 소스 SQL + 설명 (메모)
 *   Step 1..N : 사용자 prompt 로 SQL/YAML 변환 (편집 가능)
 *
 * 각 step 의 kind (KBNL-66 — 자유 사슬):
 *   - sql_refine   : SQL → SQL  (직전 SQL 결과를 base 로)
 *   - yaml_generate: SQL → YAML (Semantic / Composite View 생성)
 *   - yaml_refine  : YAML → YAML (이미 생성된 SV/CV 의 후속 수정)
 *
 * 각 step 의 mode:
 *   - cumulative  : 직전 step 의 결과 (sql/yaml) 를 base 로 (= 누적)
 *   - independent : sql_refine/yaml_generate 는 sourceSql, yaml_refine 은 첫 yaml_generate 결과
 *
 * 저장은 기존 sql_queries 컬렉션 doc 에 `workbench_steps` 필드를 추가해 동일 doc 에 누적.
 * `semantic_yaml` 필드에는 마지막 yaml_* step 의 결과를 동시에 저장 — 기존 SqlConverter 호환.
 */

/* ── YAML 결과 유형 감지 (SqlConverter 와 동일 로직) ───────────────────────── */
function detectYamlType(yamlStr) {
  if (!yamlStr || !yamlStr.trim()) return null
  const trimmed = yamlStr.trim()
  // KBNL-115: '---' 는 '문서 시작 표시'로도 쓰이므로(선행 단독 '---'),
  //   '---' 유무가 아니라 '실제 문서 조각 수(2개 이상)'로 composite 를 판정한다.
  //   (선행 '---' 1개만 있는 단일 SV 가 Composite 로 오분류되던 문제 — 리뷰)
  const docs = trimmed.split(/^---\s*$/m).map(s => s.trim()).filter(Boolean)
  const isSvDoc = (d) => /^semantic_view\s*:/m.test(d) || /^-\s*semantic_view\s*:/m.test(d)
  // 문서가 2개 이상이고 각 문서가 semantic_view → 여러 SV 묶음 = composite
  if (docs.length >= 2 && docs.every(isSvDoc)) return 'composite'
  if (/^semantic_views\s*:/m.test(trimmed)) return 'composite'
  if (/^-\s*semantic_view\s*:/m.test(trimmed)) return 'composite'
  if (/^semantic_view\s*:/m.test(trimmed)) return 'single'
  return 'unknown'
}

function typeLabel(type) {
  if (type === 'single') return 'Semantic View (단일)'
  if (type === 'composite') return 'Composite View (복합)'
  if (type === 'unknown') return '알 수 없는 형식'
  return ''
}

// extractViewSummariesFromYaml 은 utils/yamlExtract.js 로 이동 (3개 컴포넌트 공유).

function typeBadgeClass(type) {
  if (type === 'single') return 'type-single'
  if (type === 'composite') return 'type-composite'
  return 'type-unknown'
}

/* yaml_generate / yaml_refine 의 user_requirements 빌더.
 *
 * 누적 모드(cumulative)이면 이전 step 들의 프롬프트도 함께 전달해서, LLM 이
 * "어떤 의도로 다듬어졌는지" 의 맥락을 알고 변환을 수행할 수 있게 한다.
 * 독립 모드(independent)이면 현재 프롬프트만 전달.
 *
 * yaml_generate 의 경우엔 직전의 SQL 정제 의도(sql_refine prompt)를 포함.
 * yaml_refine 의 경우엔 직전의 YAML 변환 의도(yaml_* prompt)를 포함.
 * (kind 별 prior 필터링 — sql_refine 의 prompt 가 yaml_refine 에 들어가면 혼란)
 *
 * 워크벤치 description (queryDesc) 는 LLM 에 전달하지 않는다 (SqlConverter 와 일관). */
function buildPriorIntents({ steps, idx }) {
  const cur = steps[idx]
  if (!cur) return ''
  const sections = []

  // 누적 모드일 때만 — 이전 step 들의 프롬프트를 순서대로 누적
  if (cur.mode === 'cumulative') {
    // yaml_refine 은 prior yaml_* step 만; 그 외 (sql_refine / yaml_generate) 는 prior sql_refine
    const priorKinds = cur.kind === 'yaml_refine'
      ? new Set(['yaml_generate', 'yaml_refine'])
      : new Set(['sql_refine'])
    const priorPrompts = []
    for (let i = 0; i < idx; i++) {
      const s = steps[i]
      if (!s) continue
      if (!priorKinds.has(s.kind)) continue
      const p = (s.prompt || '').trim()
      if (!p) continue
      priorPrompts.push(`### Step ${i + 1} (${s.mode === 'independent' ? '독립' : '누적'} · ${labelForKind(s.kind)})\n${p}`)
    }
    if (priorPrompts.length > 0) {
      const head = cur.kind === 'yaml_refine' ? '이전 YAML 변환 단계의 의도' : '이전 SQL 정제 단계의 의도'
      sections.push(`## ${head}\n${priorPrompts.join('\n\n')}`)
    }
  }

  const curPrompt = (cur.prompt || '').trim()
  if (curPrompt) {
    sections.push(`## ${cur.kind === 'yaml_refine' ? 'YAML 정제' : '최종 변환'} 요구사항\n${curPrompt}`)
  }
  return sections.join('\n\n')
}

/* ── Step kind 라벨 헬퍼 ─────────────────────────────────────────────────── */
function labelForKind(kind) {
  if (kind === 'sql_refine')   return 'SQL 정제'
  if (kind === 'yaml_generate') return 'YAML 생성'
  if (kind === 'yaml_refine')  return 'YAML 정제'
  if (kind === 'yaml_to_sql')  return 'YAML → SQL'
  return kind || '?'
}
function shortLabelForKind(kind) {
  if (kind === 'sql_refine')   return '정제'
  if (kind === 'yaml_generate') return 'YAML'
  if (kind === 'yaml_refine')  return 'YAML정제'
  if (kind === 'yaml_to_sql')  return 'Y→SQL'
  return kind || '?'
}
function runLabelForKind(kind) {
  if (kind === 'sql_refine')   return '▶ 실행'
  if (kind === 'yaml_generate') return '▶ 변환'
  if (kind === 'yaml_refine')  return '▶ 정제'
  if (kind === 'yaml_to_sql')  return '▶ SQL 생성'
  return '▶ 실행'
}
function isYamlKind(kind) {
  return kind === 'yaml_generate' || kind === 'yaml_refine'
}
/* KBNL-99: kind 가 요구하는 입력 type ('sql' | 'yaml').
 *   - sql_refine / yaml_generate : SQL 입력
 *   - yaml_refine / yaml_to_sql  : YAML 입력 */
function inputKindOf(kind) {
  return (kind === 'yaml_refine' || kind === 'yaml_to_sql') ? 'yaml' : 'sql'
}
/* kind 가 내는 출력 type ('sql' | 'yaml').
 *   - sql_refine / yaml_to_sql   : SQL 출력
 *   - yaml_generate / yaml_refine: YAML 출력 */
function outputKindOf(kind) {
  return (kind === 'yaml_generate' || kind === 'yaml_refine') ? 'yaml' : 'sql'
}

/* KBNL-99: 로컬 파일을 텍스트로 읽어 onLoad(text) 호출하는 file input 트리거.
 * 숨긴 <input type=file> 를 동적으로 만들어 클릭 → FileReader. */
function pickLocalFile(onLoad) {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.sql,.yaml,.yml,.txt,text/*'
  input.onchange = () => {
    const file = input.files && input.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => onLoad(String(reader.result || ''))
    reader.readAsText(file)
  }
  input.click()
}

/* KBNL-99: 입력→출력 type 색 뱃지. SQL=파랑, YAML=초록.
 * step 헤드 / STEPS 네비 양쪽에서 동일하게 써서 타입을 직관적으로 보여줌. */
function TypeFlowBadge({ kind, size = 'normal' }) {
  const tin = inputKindOf(kind)
  const tout = outputKindOf(kind)
  const chip = (t) => ({
    display: 'inline-block',
    padding: size === 'xs' ? '0 3px' : '0 5px',
    fontSize: size === 'xs' ? 9 : 10,
    fontWeight: 700,
    lineHeight: size === 'xs' ? '13px' : '15px',
    borderRadius: 3,
    color: '#fff',
    background: t === 'yaml' ? '#16a34a' : '#2563eb',  // YAML 초록 / SQL 파랑
    letterSpacing: '0.3px',
  })
  return (
    <span className="wb-typeflow-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }} title={`${tin.toUpperCase()} → ${tout.toUpperCase()}`}>
      <span style={chip(tin)}>{tin.toUpperCase()}</span>
      <span style={{ fontSize: size === 'xs' ? 9 : 10, opacity: 0.6 }}>→</span>
      <span style={chip(tout)}>{tout.toUpperCase()}</span>
    </span>
  )
}

/* ── Step 객체 생성 헬퍼 ─────────────────────────────────────────────────── */
function newStepId() {
  return `wb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

// KBNL-103: Step 0(소스)도 일반 step 과 동일하게 변환 가능하게 통합.
// step0 는 가상 인덱스 -1 로 다뤄 기존 step 핸들러(patchStep/runStep/setStepKind 등)를
// 그대로 재사용한다. (-1 = step0, 0..N = steps[])
const STEP0_IDX = -1

// KBNL-112: 패널 버튼 정리 — 로컬 파일 업로드(⤴ 파일)/다운로드(⤓ 다운로드)는
//   잘 안 쓰여 숨김(코드는 보존). 다시 노출하려면 true 로.
const SHOW_LOCAL_FILE_IO = false

function makeStep({ kind = 'sql_refine', mode = 'cumulative' } = {}) {
  return {
    id: newStepId(),
    kind,                   // 'sql_refine' | 'yaml_generate' | 'yaml_refine'
    mode,                   // 'cumulative' | 'independent'
    prompt: '',
    base_sql: '',           // 실행 시점 입력 스냅샷 (sql_refine / yaml_generate 의 입력 SQL)
    base_yaml: '',          // 실행 시점 입력 스냅샷 (yaml_refine 의 입력 YAML)
    result_sql: '',         // sql_refine 출력
    result_yaml: '',        // yaml_generate / yaml_refine 출력
    edited: false,
    status: 'idle',         // 'idle' | 'running' | 'done' | 'error'
    error: null,
    updated_at: '',
    // KBNL-99: step 별 SV/CV 저장 옵션. yaml step (yaml_generate / yaml_refine) 에만 의미.
    save_as_view: false,    // true 이면 워크벤치 저장 시 result_yaml 을 별도 SV/CV doc 로 저장
    view_name: '',          // 비우면 자동 = '${queryName}_step${N}' 또는 stepNo 만
    // KBNL-99: 좌측 '변환 전' (base) 패널을 사용자가 직접 편집한 경우 우선 사용.
    // 비어 있으면 resolveBase 자동 결과 사용. yaml_refine / yaml_to_sql 에서만 의미.
    base_override: '',
    // KBNL-120: yaml_to_sql 변환 엔진 — 'rule'(기본, 결정적 규칙 변환) | 'llm'.
    // yaml_to_sql kind 에서만 의미. 규칙=정합성/재현성, LLM=유연성/가독성/지시 반영.
    engine: 'rule',
  }
}

/* ── 옛 포맷(is_final) → 새 kind axis 마이그레이션 ────────────────────────── */
function migrateStepShape(s) {
  // 새 포맷이면 그대로
  if (s.kind === 'sql_refine' || s.kind === 'yaml_generate' || s.kind === 'yaml_refine' || s.kind === 'yaml_to_sql') {
    return {
      id:          s.id || newStepId(),
      kind:        s.kind,
      mode:        s.mode === 'independent' ? 'independent' : 'cumulative',
      prompt:      s.prompt || '',
      base_sql:    s.base_sql || '',
      base_yaml:   s.base_yaml || '',
      result_sql:  s.result_sql || '',
      result_yaml: s.result_yaml || '',
      edited:      !!s.edited,
      status:      (s.result_sql || s.result_yaml) ? 'done' : 'idle',
      error:       null,
      updated_at:  s.updated_at || '',
      // KBNL-99: step 별 SV/CV 저장 옵션 — 옛 doc 엔 없을 수 있어 default false.
      save_as_view: !!s.save_as_view,
      view_name:    s.view_name || '',
      base_override: s.base_override || '',
      // KBNL-120: yaml_to_sql 엔진 — 옛 doc 엔 없을 수 있어 default 'rule'.
      engine:       s.engine === 'llm' ? 'llm' : 'rule',
    }
  }
  // 옛 포맷 (is_final boolean)
  const isFinal = !!s.is_final
  return {
    id:          s.id || newStepId(),
    kind:        isFinal ? 'yaml_generate' : 'sql_refine',
    mode:        s.mode === 'independent' ? 'independent' : 'cumulative',
    prompt:      s.prompt || '',
    base_sql:    s.base_sql || '',
    base_yaml:   '',
    result_sql:  s.result_sql || '',
    result_yaml: s.result_yaml || '',
    edited:      !!s.edited,
    status:      (s.result_sql || s.result_yaml) ? 'done' : 'idle',
    error:       null,
    updated_at:  s.updated_at || '',
    save_as_view: false,
    view_name:    '',
    base_override: '',
    engine: 'rule',
  }
}

/* ── steps 배열의 "직전 base" 결정 ─────────────────────────────────────────
 * 반환: { kind: 'sql' | 'yaml', value: string }
 *
 * step.kind 별 입력 type:
 *   - sql_refine / yaml_generate : SQL 입력
 *   - yaml_refine                : YAML 입력
 */
function resolveBase(sourceSql, steps, idx, kind, mode, step0 = null) {
  // KBNL-99: 소스(sourceSql)는 단일 텍스트. SQL 입력 step 은 SQL 로, YAML 입력
  // step 은 YAML 로 해석해 base fallback 으로 쓴다.
  // KBNL-103: step0 는 steps[] 보다 앞선 "가상 상위 step". cumulative chain 에서
  //   steps[] 를 모두 본 뒤, sourceSql 로 폴백하기 전에 step0 의 결과를 후보로 본다.
  //   (Step 0 에서 변환을 했다면 그 결과가 다음 step 의 base 가 됨)
  // SQL 입력 종류 (sql_refine, yaml_generate)
  if (kind === 'sql_refine' || kind === 'yaml_generate') {
    if (mode === 'independent') return { kind: 'sql', value: sourceSql }
    // cumulative: 가장 가까운 직전 SQL 출력 (sql_refine / yaml_to_sql 의 result_sql).
    for (let i = idx - 1; i >= 0; i--) {
      const s = steps[i]
      if (!s) continue
      if (outputKindOf(s.kind) === 'sql' && s.result_sql && s.result_sql.trim()) {
        return { kind: 'sql', value: s.result_sql }
      }
    }
    // step0 의 SQL 출력 (Step 0 가 sql_refine / yaml_to_sql 로 변환된 경우)
    if (step0 && outputKindOf(step0.kind) === 'sql' && step0.result_sql && step0.result_sql.trim()) {
      return { kind: 'sql', value: step0.result_sql }
    }
    return { kind: 'sql', value: sourceSql }
  }

  // YAML 입력 종류 (yaml_refine, yaml_to_sql)
  if (kind === 'yaml_refine' || kind === 'yaml_to_sql') {
    if (mode === 'independent') {
      // 첫 번째 yaml_generate 의 결과 = "원본 YAML 시안", 없으면 소스를 YAML 로.
      // step0 가 yaml_generate 라면 그게 가장 앞선 "원본 YAML 시안".
      if (step0 && step0.kind === 'yaml_generate' && step0.result_yaml && step0.result_yaml.trim()) {
        return { kind: 'yaml', value: step0.result_yaml }
      }
      for (let i = 0; i < idx; i++) {
        const s = steps[i]
        if (s && s.kind === 'yaml_generate' && s.result_yaml && s.result_yaml.trim()) {
          return { kind: 'yaml', value: s.result_yaml }
        }
      }
      return { kind: 'yaml', value: sourceSql }
    }
    // cumulative: 가장 가까운 직전 YAML 출력 (yaml_generate / yaml_refine), 없으면 소스.
    for (let i = idx - 1; i >= 0; i--) {
      const s = steps[i]
      if (!s) continue
      if (isYamlKind(s.kind) && s.result_yaml && s.result_yaml.trim()) {
        return { kind: 'yaml', value: s.result_yaml }
      }
    }
    // step0 의 YAML 출력 (Step 0 가 yaml_generate / yaml_refine 로 변환된 경우)
    if (step0 && isYamlKind(step0.kind) && step0.result_yaml && step0.result_yaml.trim()) {
      return { kind: 'yaml', value: step0.result_yaml }
    }
    return { kind: 'yaml', value: sourceSql }
  }

  return { kind: 'sql', value: sourceSql }
}

/* ── 마지막 yaml_* step (저장될 SV/CV) ────────────────────────────────────── */
function findLastYamlStep(steps) {
  for (let i = steps.length - 1; i >= 0; i--) {
    const s = steps[i]
    if (s && isYamlKind(s.kind) && s.result_yaml && s.result_yaml.trim()) {
      return s
    }
  }
  return null
}

/* KBNL-99: step 사이 임의 위치에 삽입하는 + 버튼 + 팝오버 메뉴.
 * '+ step' 클릭 시 그 위치 옆에 떠오르는 팝오버에서 4개 kind 를 색 뱃지 +
 * 라벨로 선택 → 즉시 삽입. 사이드바 폭 제약을 벗어난 absolute 팝오버.
 * insertAt = 새 step 의 인덱스 (0=맨 앞, steps.length=맨 끝). */
function WbStepInsertBetween({ insertAt, steps, onInsert, compact = false }) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  // KBNL-99: 소스가 단일 텍스트라 yaml 입력 step 은 어디서든 소스를 YAML 로 해석
  // 가능 → 위치 제약 없음. 모든 kind 항상 선택 가능.

  // KBNL: 팝오버를 position:fixed 로 띄워 사이드바 overflow 클리핑/화면 밖 잘림 방지.
  // 좌표는 트리거(wrapRef) 기준으로 계산하고 뷰포트 경계로 클램프한다.
  const [pos, setPos] = useState(null)
  // 트리거 위치 측정 → 팝오버 좌표. '열기' 이벤트에서 호출(effect 내 setState 회피).
  const computePos = () => {
    const el = wrapRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    const POP_W = 190
    const POP_H = 176   // 헤더 + 4개 항목 대략치 (하단 잘림 방지용)
    // compact(사이드바)는 트리거 왼쪽에 펼침, 일반은 트리거 아래 중앙
    let left = compact ? r.left - POP_W - 6 : r.left + r.width / 2 - POP_W / 2
    let top  = compact ? r.top : r.bottom + 4
    // 뷰포트 밖(오른쪽/아래)으로 나가지 않게 클램프
    left = Math.max(8, Math.min(left, window.innerWidth  - POP_W - 8))
    top  = Math.max(8, Math.min(top,  window.innerHeight - POP_H - 8))
    return { left, top, width: POP_W }
  }
  const toggleOpen = () => {
    const next = !open
    setOpen(next)
    setPos(next ? computePos() : null)
  }
  // 스크롤/리사이즈 시 위치가 어긋나므로 닫는다. setState 는 이벤트 콜백에서만(effect 본문 X).
  useEffect(() => {
    if (!open) return
    const close = () => setOpen(false)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true)
    return () => {
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])

  // 외부 클릭 시 닫기 (팝오버는 fixed 여도 wrapRef DOM 하위라 contains 로 판별됨)
  useEffect(() => {
    if (!open) return
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const KIND_ITEMS = [
    { kind: 'sql_refine',   label: 'SQL 정제' },
    { kind: 'yaml_generate', label: 'YAML 생성' },
    { kind: 'yaml_refine',  label: 'YAML 정제' },
    { kind: 'yaml_to_sql',  label: 'YAML → SQL' },
  ]

  const handlePick = (kind) => {
    onInsert(insertAt, kind)
    setOpen(false)
  }

  // 트리거 버튼 — compact (사이드바) / 일반 (가로 라인) 두 형태
  const trigger = compact ? (
    <button
      type="button"
      className="wb-stepnav-insert"
      onClick={toggleOpen}
      title="이 위치에 새 step 삽입"
      style={{
        // KBNL-125/129 후속: var(--accent)=KB노랑이라 라이트 테마에서 노란 글씨가
        //   안 보임(사용자 피드백) → 테마 무관 파랑 고정 + 틴트 배경 + 굵은 글씨.
        display: 'block', width: '100%', margin: '3px 0', padding: '2px 0',
        fontSize: 11, lineHeight: '16px', fontWeight: 700,
        color: '#3b82f6',
        border: '1px dashed #3b82f6',
        background: 'rgba(59, 130, 246, 0.15)',
        borderRadius: 4, cursor: 'pointer',
      }}
    >+ step</button>
  ) : (
    <div className="wb-step-insert-gap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '2px 0' }}>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <button
        type="button"
        onClick={toggleOpen}
        title="이 위치에 새 step 삽입"
        style={{
          // KBNL-125/129 후속: 테마 무관 파랑 고정 (accent=KB노랑은 라이트에서 가독 불가).
          fontSize: 11, fontWeight: 600, padding: '0 8px',
          color: '#3b82f6',
          border: '1px dashed #3b82f6',
          background: 'rgba(59, 130, 246, 0.13)',
          borderRadius: 4, cursor: 'pointer',
        }}
      >+ 여기에 step 삽입</button>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  )

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {trigger}
      {open && pos && (
        <div
          className="wb-insert-popover"
          style={{
            // KBNL: fixed + 계산 좌표 → 사이드바 overflow/화면 밖으로 잘리지 않음
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            width: pos.width,
            zIndex: 1000,
            minWidth: 180,
            background: 'var(--bg, #fff)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            padding: 4,
          }}
        >
          <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '2px 6px 4px' }}>
            여기에 새 step 삽입
          </div>
          {KIND_ITEMS.map(it => (
            <button
              key={it.kind}
              type="button"
              className="wb-insert-popover-item"
              onClick={() => handlePick(it.kind)}
              title={it.label}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '5px 8px', border: 'none', borderRadius: 4,
                background: 'transparent', cursor: 'pointer',
                textAlign: 'left', fontSize: 12,
              }}
            >
              <TypeFlowBadge kind={it.kind} size="xs" />
              <span>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}


export default function SqlConverterWorkbench({ showToast, storageSettings, editorTheme, settings, onNavigateToView }) {
  // ── 저장된 SQL 목록 (SqlConverter 와 공유 — 동일 컬렉션) ──────────────
  const [savedQueries, setSavedQueries] = useState([])
  const [selectedQuery, setSelectedQuery] = useState(null)
  const [selectedQueryIsMine, setSelectedQueryIsMine] = useState(true)
  // KBNL-141(리뷰): 협업 모드 동명이인 충돌 방지 — 선택/리네임 상태를 owner 까지 구분.
  const [selectedQueryOwner, setSelectedQueryOwner] = useState('')
  // KBNL-69: 본문 헤더 토큰 배지 (sql_text 기준)
  const [selectedQueryTokenCount, setSelectedQueryTokenCount] = useState(null)
  const [sqlQuery, setSqlQuery] = useState('')   // 좌측 검색 필터
  // KBNL-141: 목록 소유자 필터 — 기본 '내것만'(false=others 숨김).
  const [showOthersWb, setShowOthersWb] = useState(false)
  // KBNL-141: 내가 돌리는 워크벤치 변환(running) — 좌측 목록 항목에 스피너 매칭용.
  const runningJobs = useRunningJobs(true)

  // KBNL-99 §7: GCS Export 다이얼로그 — 어느 step 결과를 내보내는지 source 객체 형태.
  // null 이면 닫힘. { content, defaultFilename, contentType, title } 으로 다이얼로그 띄움.
  const [exportSource, setExportSource] = useState(null)
  // KBNL-99: GCS Import 다이얼로그 — { onImported } 형태. null 이면 닫힘.
  const [importTarget, setImportTarget] = useState(null)
  // KBNL-101: step 별 컬럼/테이블 검증 상태 — { [stepId]: {running, result} }
  const [validateState, setValidateState] = useState({})

  // KBNL-99 §8: 좌측 패널 (Saved SQL Queries) 숨김 토글 — 결과 집중 모드용.
  // sessionStorage 에 저장해 같은 세션 동안 유지.
  const [leftCollapsed, setLeftCollapsed] = useState(() => {
    try { return sessionStorage.getItem('wb-left-collapsed') === '1' } catch { return false }
  })
  const toggleLeftCollapsed = useCallback(() => {
    setLeftCollapsed(prev => {
      const next = !prev
      try { sessionStorage.setItem('wb-left-collapsed', next ? '1' : '0') } catch { /* sessionStorage 불가 환경 무시 */ }
      return next
    })
  }, [])

  // ── 워크벤치 본체 상태 ─────────────────────────────────────────────────
  const [queryName, setQueryName] = useState('')
  const [queryDesc, setQueryDesc] = useState('')
  const [viewName,  setViewName]  = useState('')   // 최종 YAML 저장 시 별도 View 이름 (비우면 queryName)
  // KBNL-99: Step 0 = 단일 '소스' 텍스트 (SQL/YAML 구분 없음). 저장 시 sql_text 필드
  // 재사용 (호환). 첫 step 의 kind 가 이 소스를 SQL 로 읽을지 YAML 로 읽을지 결정.
  const [sourceSql, setSourceSql] = useState('')   // Step 0 — 소스 텍스트(= Step 0 의 '변환 전')
  // KBNL-103: Step 0 의 변환 설정/결과 (kind/prompt/result_*/save_as_view 등).
  // base(변환 전)는 항상 sourceSql 이므로 mode 는 의미 없음(독립 고정). 기본 kind 는
  // 워크벤치 주력 동작인 yaml_generate(SQL→SV) + 결과를 SV 로 저장(save_as_view).
  const [step0, setStep0] = useState(() => ({ ...makeStep({ kind: 'yaml_generate' }), save_as_view: true }))
  // KBNL-99: 소스칸 하이라이트 override. null=자동(첫 step 입력 타입), 'sql'|'yaml'=수동 고정.
  const [highlightOverride, setHighlightOverride] = useState(null)
  const [steps,     setSteps]     = useState([])   // Step 1..N
  // KBNL-105 (approach B): 진행 중 변환이 프로젝트 전환에도 멈추지 않게 — 워크벤치별
  //   상태 스냅샷 캐시 + run-by-key. wbKeyRef = 현재 활성 워크벤치 키(저장본=name,
  //   미저장 신규=new-N). projectCacheRef = 키별 상태 스냅샷(전환 시 보관/복귀 시 복원).
  //   runStep 은 시작 시점 키를 캡처해, 완료 시 활성이면 라이브 반영·아니면 캐시에 반영.
  const wbKeyRef = useRef('new-0')
  const newWbSeqRef = useRef(0)
  const projectCacheRef = useRef(new Map())
  // ── KBNL-106: 창 간 '변환 중' 공유 (advisory 리스) ─────────────────────────
  // 탭 식별자 — 탭마다 고유, 새로고침에도 유지 (sessionStorage).
  const tabIdRef = useRef(null)
  if (tabIdRef.current === null) {
    try {
      let v = sessionStorage.getItem('wb-tab-id')
      if (!v) {
        v = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `t${Date.now()}${Math.random().toString(36).slice(2)}`
        sessionStorage.setItem('wb-tab-id', v)
      }
      tabIdRef.current = v
    } catch { tabIdRef.current = `t${Date.now()}${Math.random().toString(36).slice(2)}` }
  }
  // 리스 refcount (키별 로컬 실행 step 수) + 하트비트 타이머.
  const leaseCountRef = useRef(new Map())
  const leaseTimerRef = useRef(null)
  // 타 창에서 같은 워크벤치 변환 중이면 {user_name, step_label} — 배너+▶ 비활성.
  const [remoteRun, setRemoteRun] = useState(null)
  // KBNL-126: 현재 워크벤치의 서버측 잡 목록 — 미회수 결과/중단/에러 배너용.
  const [wbJobs, setWbJobs] = useState([])
  // KBNL-141(리뷰): 진행 중 변환 강제 중단용 — jobId → AbortController.
  //   워크벤치 변환은 요청 유지형(_run_with_job_mirror)이라, 클라에서 fetch 를 abort 해야
  //   응답이 돌아와도 결과가 적용되지 않는다(중단이 실제로 결과를 막음).
  const runCtrlRef = useRef(new Map())

  // wbKey(`<owner>::<name>`)에서 리스 식별자 추출. 미저장(new-N)은 리스 미적용.
  const leaseIdentityOf = (wbKey) => {
    const i = (wbKey || '').indexOf('::')
    if (i < 0) return null
    return { owner: wbKey.slice(0, i), name: wbKey.slice(i + 2) }
  }
  // 하트비트: 로컬 실행 중인 리스가 있으면 15s 마다 갱신 (TTL 45s).
  const ensureLeaseHeartbeat = () => {
    if (leaseTimerRef.current) return
    leaseTimerRef.current = setInterval(() => {
      const entries = [...leaseCountRef.current.entries()].filter(([, n]) => n > 0)
      if (entries.length === 0) { clearInterval(leaseTimerRef.current); leaseTimerRef.current = null; return }
      for (const [k] of entries) {
        const ident = leaseIdentityOf(k)
        if (ident) api.wbRunLeaseHeartbeat({ name: ident.name, owner_user_id: ident.owner, holder_id: tabIdRef.current }).catch(() => {})
      }
    }, 15000)
  }
  const releaseLease = (ident) => {
    const k = `${ident.owner}::${ident.name}`
    const n = (leaseCountRef.current.get(k) || 1) - 1
    leaseCountRef.current.set(k, Math.max(0, n))
    if (n <= 0) {
      api.wbRunLeaseRelease({ name: ident.name, owner_user_id: ident.owner, holder_id: tabIdRef.current }).catch(() => {})
    }
  }
  // KBNL-99: 활성 step. 'step-0' = 소스, 'step-${id}' = steps[i] 의 step.
  // 우측 STEPS 네비게이션이 setActiveStepKey 로만 전환. 본문은 활성 step 만 렌더.
  const [activeStepKey, setActiveStepKey] = useState('step-0')
  // KBNL-136(feedback 20260618): 두 편집기(변환 전/후) 스크롤 동기화 토글.
  // 라인번호 정렬 — 한쪽 최상단 라인 N 에 맞춰 다른쪽도 라인 N 을 최상단으로.
  const [scrollSync, setScrollSync] = useState(false)
  const wbRootRef = useRef(null)
  // KBNL-99 후속: 설명 / 프롬프트 영역을 기본 1줄로 축소하고 토글 시 펼침.
  const [descExpanded, setDescExpanded] = useState(false)
  const [promptExpandedSet, setPromptExpandedSet] = useState(() => new Set())
  const togglePromptExpanded = useCallback((sid) => {
    setPromptExpandedSet(prev => {
      const s = new Set(prev)
      if (s.has(sid)) s.delete(sid); else s.add(sid)
      return s
    })
  }, [])

  // ── 동명 view 중복 체크 (최종 YAML 저장 충돌 경고) ────────────────────
  const [savedMetricViews,    setSavedMetricViews]    = useState([])
  const [savedCompositeViews, setSavedCompositeViews] = useState([])
  const [overwriteDialog,     setOverwriteDialog]     = useState(null)
  // KBNL-108: 패널 type별 Firestore 저장 — 이름 입력 팝업 상태.
  //   { kind: 'sql'|'sv'|'cv', content, defaultName } | null
  const [saveAsDialog, setSaveAsDialog] = useState(null)
  const [saveAsBusy,   setSaveAsBusy]   = useState(false)
  // KBNL-111: 패널 📂 불러오기 — Firestore 검색 목록 팝업. { isYaml, onLoaded } | null
  const [loadPicker,       setLoadPicker]       = useState(null)
  const [loadPickerSearch, setLoadPickerSearch] = useState('')
  const [loadPickerBusy,   setLoadPickerBusy]   = useState(false)

  // 로드 시점 View 정보 — 의도된 갱신 vs 우연한 덮어쓰기 구분용
  const [loadedView, setLoadedView] = useState(null) // { name, type } | null

  // ── 이름 바꾸기 (좌측 목록 더블클릭 → inline input) ──────────────────
  const [renamingQuery, setRenamingQuery] = useState(null)
  const [renameValue,   setRenameValue]   = useState('')

  // ── 복제 (Duplicate) ───────────────────────────────────────────────────
  // 사이드바 항목별 hover 액션. 다이얼로그에서 새 이름 확인 후
  // 원본 SQL doc (workbench_steps 포함) + 연결 SV/CV 까지 함께 복제한다.
  const [duplicateDialog, setDuplicateDialog] = useState(null) // { sourceName, defaultName } | null
  const [duplicating, setDuplicating] = useState(false)

  // ── 비동기 상태 ────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false)

  // ── KBNL-74: 기존 SV 추천 모달 ────────────────────────────────────────
  // 검색 기준은 yaml 의 종류에 따라 다르다:
  //   - 변환 전 / unknown : sourceSql 기반 (mode='sql')
  //   - single SV         : 그 SV 의 name+description+tables 기반 (mode='query')
  //   - composite (CV)    : sub-SV picker → 선택한 SV 기반 (mode='query')
  //
  // Workbench 는 picker trigger 가 여러 곳 (View 이름 행 + 각 yaml step 의 result-head).
  // recommendPickerKey 로 어느 picker 가 열렸는지 식별 ('top' | 'step-N').
  // pickerRefs map 으로 외부 클릭 감지 — 활성 picker 의 DOM 만 contains 체크.
  const [recommendOpen, setRecommendOpen] = useState(false)
  // 랭킹 품질: 변환 결과 SV 는 query 텍스트(name+desc+테이블명)가 아니라
  // seed_sv + override_yaml 로 검색 — SV 탭과 동일 품질 (vector 질의가 풍부해지고
  // identifier 채널 활성 + 저장된 동명 SV 는 exclude). { name, description, yaml } | null
  const [recommendSeed, setRecommendSeed] = useState(null)
  const [recommendPickerKey, setRecommendPickerKey] = useState(null)
  const pickerRefs = useRef({})

  // ── CodeMirror 확장 ───────────────────────────────────────────────────
  const sqlExtensions = useMemo(() => [sql(), search({ top: true }), Prec.highest(keymap.of([
    ...searchKeymap,
    { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
  ]))], [])
  const yamlExtensions = useMemo(() => [yaml(), search({ top: true }), Prec.highest(keymap.of([
    ...searchKeymap,
    { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true },
  ]))], [])

  // ── 저장된 SQL 목록 로드 ───────────────────────────────────────────────
  const loadQueries = useCallback(async () => {
    try {
      const list = await api.listSqlQueries(storageSettings)
      setSavedQueries(list)
    } catch (e) {
      console.error('Failed to load SQL queries:', e)
      showToast('error', 'SQL 쿼리 목록 로드 실패: ' + e.message)
    }
  }, [storageSettings, showToast])

  useEffect(() => { loadQueries() }, [loadQueries])

  // KBNL-136(feedback 20260618): 두 편집기 스크롤 동기화 — 논리 줄 기준(방안 A).
  // 워드랩이 켜지면 긴 논리 줄이 여러 시각 행으로 접혀 양쪽 누적 픽셀 높이가 달라지므로
  // 픽셀 1:1 동기화는 줄이 어긋난다. 그래서 src 최상단 '논리 줄 번호 + 가려진 offset'을
  // 구해 dst 도 같은 줄로 맞춘다(⤒ 1회 맞춤과 동일). 짧은 쪽은 끝 줄에서 멈춤(clamp).
  // 에디터는 렌더 직후 비동기 mount 라 rAF 로 짧게 폴링하며 뷰가 잡힐 때 배선.
  useEffect(() => {
    if (!scrollSync) return undefined
    const root = wbRootRef.current
    if (!root) return undefined
    let cleanup = null
    let raf = 0
    let tries = 0
    const wire = () => {
      const split = root.querySelector('.wb-step-split')
      const eds = split ? split.querySelectorAll('.cm-editor') : []
      const a = eds[0] ? EditorView.findFromDOM(eds[0]) : null
      const b = eds[1] ? EditorView.findFromDOM(eds[1]) : null
      if (!a || !b) {
        if (tries++ < 60) raf = requestAnimationFrame(wire)
        return
      }
      // 방안 A: 논리 줄 기준 동기화 (워드랩 대응). src 최상단 논리 줄 N + 가려진
      // offset 을 구해, dst 도 같은 줄 N 이 같은 만큼 가려지도록 정렬. 워드랩으로
      // 양쪽 시각 행 수가 달라도 논리 줄은 정확히 맞는다. (⤒ 1회 맞춤과 동일 로직)
      // 되튐 방지: 프로그래밍적 스크롤이 만든 이벤트를 pending 카운터로 1개 소비.
      let pending = 0
      const make = (src, dst) => () => {
        if (pending > 0) { pending--; return }   // 우리가 만든 스크롤 — 소비하고 무시
        try {
          const top = src.scrollDOM.scrollTop
          const block = src.lineBlockAtHeight(top)
          const offset = top - block.top
          const lineNo = src.state.doc.lineAt(block.from).number
          const dstLine = Math.min(lineNo, dst.state.doc.lines)
          const dstBlock = dst.lineBlockAt(dst.state.doc.line(dstLine).from)
          const target = dstBlock.top + offset
          if (Math.abs(dst.scrollDOM.scrollTop - target) < 0.5) return  // 이미 정렬됨
          pending++
          dst.scrollDOM.scrollTop = target
        } catch { /* 레이아웃 전환 중 등 — 무시 */ }
      }
      const ha = make(a, b)
      const hb = make(b, a)
      a.scrollDOM.addEventListener('scroll', ha, { passive: true })
      b.scrollDOM.addEventListener('scroll', hb, { passive: true })
      cleanup = () => {
        a.scrollDOM.removeEventListener('scroll', ha)
        b.scrollDOM.removeEventListener('scroll', hb)
      }
    }
    wire()
    return () => { if (raf) cancelAnimationFrame(raf); if (cleanup) cleanup() }
    // activeStepKey/steps 변경 시 split 의 에디터가 교체되므로 재배선
  }, [scrollSync, activeStepKey, steps])

  // KBNL-136(feedback 20260618): 1회성 줄 맞춤 — 편집기 툴바 '⤒' 버튼이 호출.
  // src(이 버튼이 속한 편집기)의 최상단 줄 N 에 맞춰, 같은 split 의 반대쪽 편집기를
  // 줄 N 이 최상단에 오도록 1회 스크롤 (동기화 토글과 무관하게 즉시 1회).
  const alignPeerToView = useCallback((src) => {
    if (!src) return
    const root = wbRootRef.current
    const split = root?.querySelector('.wb-step-split')
    const eds = split ? split.querySelectorAll('.cm-editor') : []
    const v0 = eds[0] ? EditorView.findFromDOM(eds[0]) : null
    const v1 = eds[1] ? EditorView.findFromDOM(eds[1]) : null
    const dst = src === v0 ? v1 : (src === v1 ? v0 : null)
    if (!dst) return
    try {
      const top = src.scrollDOM.scrollTop
      const block = src.lineBlockAtHeight(top)
      // 최상단 줄이 반쯤 가려진 양(부분 스크롤)을 반대쪽에도 동일하게 반영 —
      // 안 그러면 줄의 top 에만 붙어 가려진 만큼 어긋난다. offset = scrollTop - 줄 top.
      const offset = top - block.top
      const lineNo = src.state.doc.lineAt(block.from).number
      const dstLine = Math.min(lineNo, dst.state.doc.lines)
      const dstBlock = dst.lineBlockAt(dst.state.doc.line(dstLine).from)
      dst.scrollDOM.scrollTop = dstBlock.top + offset
    } catch { /* 레이아웃 전환 중 등 — 무시 */ }
  }, [])

  // KBNL-106: 현재 열린 (저장된) 워크벤치의 타 창 '변환 중' 상태 폴링 (5s).
  //   다른 창/브라우저가 같은 워크벤치를 변환 중이면 배너 + ▶ 비활성.
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      const ident = leaseIdentityOf(wbKeyRef.current)
      if (!ident) { if (!cancelled) setRemoteRun(null); return }
      try {
        const st = await api.wbRunLeaseStatus(ident.name, ident.owner)
        if (cancelled) return
        if (st?.running && st.holder_id !== tabIdRef.current) {
          setRemoteRun({ user_name: st.user_name || '', step_label: st.step_label || '' })
        } else {
          setRemoteRun(null)
        }
      } catch { /* 폴링 실패 무시 (다음 tick) */ }
    }
    tick()
    const t = setInterval(tick, 5000)
    return () => { cancelled = true; clearInterval(t) }
    // KBNL-141(리뷰): selectedQuery+owner 변경 = 프로젝트 전환 시 즉시 재조회
    //   (같은 이름 다른 owner 로 전환해도 다음 tick 까지 안 기다리고 즉시 갱신).
  }, [selectedQuery, selectedQueryOwner])

  // KBNL-126: 서버측 잡 폴링 (10s) — 미회수 결과(done)/중단(interrupted)/에러 배너.
  //   새로고침·브라우저 종료 후 재접속해도 진행 중/완료된 변환을 여기서 발견·회수.
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      const ident = leaseIdentityOf(wbKeyRef.current)
      if (!ident) { if (!cancelled) setWbJobs([]); return }
      try {
        const res = await api.wbJobsList(ident.name, ident.owner)
        if (!cancelled) setWbJobs(Array.isArray(res?.jobs) ? res.jobs : [])
      } catch { /* 폴링 실패 무시 (다음 tick) */ }
    }
    tick()
    const t = setInterval(tick, 10000)
    return () => { cancelled = true; clearInterval(t) }
  }, [selectedQuery, selectedQueryOwner])

  // 저장된 Metric / Composite View — 동명 중복 체크용 (UI 노출 X)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [metrics, composites] = await Promise.all([
          api.listViews(storageSettings).catch(() => []),
          api.listCompositeViews(storageSettings).catch(() => []),
        ])
        if (!cancelled) {
          setSavedMetricViews(metrics)
          setSavedCompositeViews(composites)
        }
      } catch (e) {
        console.error('Failed to load views:', e)
      }
    })()
    return () => { cancelled = true }
  }, [storageSettings])

  // ── SQL 선택(로드) ─────────────────────────────────────────────────────
  // KBNL-105: 현재 워크벤치 전체 상태를 매 렌더 ref 에 미러링 — 클로저 staleness 차단.
  //   (메모이즈된 콜백이 옛 state 를 캡처해 진행 중 상태를 놓치는 문제 방지.)
  const liveStateRef = useRef({})
  liveStateRef.current = {
    selectedQuery, selectedQueryIsMine, selectedQueryOwner, selectedQueryTokenCount,
    queryName, queryDesc, viewName, sourceSql, step0, steps,
    highlightOverride, activeStepKey, loadedView,
  }
  const captureSnapshot = useCallback(() => ({ ...liveStateRef.current }), [])

  // KBNL-105: 캐시 스냅샷에서 상태 복원 (전환했던 프로젝트로 복귀 — 진행 중/결과 보존).
  const restoreSnapshot = useCallback((snap) => {
    setSelectedQuery(snap.selectedQuery)
    setSelectedQueryIsMine(snap.selectedQueryIsMine)
    setSelectedQueryOwner(snap.selectedQueryOwner || '')
    setSelectedQueryTokenCount(snap.selectedQueryTokenCount)
    setQueryName(snap.queryName)
    setQueryDesc(snap.queryDesc)
    setViewName(snap.viewName)
    setSourceSql(snap.sourceSql)
    setStep0(snap.step0)
    setSteps(snap.steps)
    setHighlightOverride(snap.highlightOverride)
    setActiveStepKey(snap.activeStepKey)
    setLoadedView(snap.loadedView)
  }, [])

  const handleSelectQuery = useCallback(async (name, ownerUserId = '') => {
    // KBNL-117: 캐시 키에 owner 포함 — 협업 모드에서 타인의 '동명' 워크벤치와
    //   캐시가 섞여 잘못된 스냅샷이 복원되던 문제 방지.
    const cacheKey = `${ownerUserId}::${name}`
    // KBNL-105: 떠나는 워크벤치 상태를 캐시에 스냅샷 (미저장 변환/편집/진행 중 보존).
    projectCacheRef.current.set(wbKeyRef.current, captureSnapshot())
    // 이번 세션에 이미 연 워크벤치는 in-memory 캐시(저장 안 한 변환 결과·편집 포함)를
    //   복원한다 — 서버 재로드 시 미저장 결과가 사라지는 문제 방지. 처음 여는 것만 서버 로드.
    const cached = projectCacheRef.current.get(cacheKey)
    if (cached) {
      wbKeyRef.current = cacheKey
      restoreSnapshot(cached)
      return
    }
    wbKeyRef.current = cacheKey
    try {
      const data = await api.getSqlQuery(name, storageSettings, ownerUserId)
      projectCacheRef.current.delete(cacheKey)   // 서버에서 새로 로드 — 묵은 캐시 제거
      setSelectedQuery(name)
      setSelectedQueryIsMine(data.is_mine !== false)
      setSelectedQueryOwner(data.owner_user_id || data.user_id || ownerUserId || '')
      setSelectedQueryTokenCount(data.token_count ?? null)
      setQueryName(data.name || name)
      setQueryDesc(data.description || '')
      setSourceSql(data.sql_text || '')
      // KBNL-103: Step 0 변환 설정/결과 복원. 옛 doc(workbench_step0 없음)은
      // Step 0 를 소스 전용으로 쓰던 것 → 기본 step0(kind yaml_generate, 결과 없음)으로.
      const loadedStep0 = data.workbench_step0
        ? migrateStepShape(data.workbench_step0)
        : { ...makeStep({ kind: 'yaml_generate' }), save_as_view: true }
      setStep0(loadedStep0)
      const loadedViewNameRaw = data.conversion_settings?.view_name || ''
      setViewName(loadedViewNameRaw)

      // workbench_steps 가 있으면 그대로 복원 — KBNL-66 마이그레이션 (is_final → kind)
      const wbSteps = Array.isArray(data.workbench_steps) ? data.workbench_steps : []
      if (wbSteps.length > 0) {
        const migrated = wbSteps.map(migrateStepShape)
        // KBNL-99: 옛 doc 호환 — save_as_view 옵션이 없던 시절 doc 이라면
        // 마지막 yaml step 을 save_as_view=true 로 셋팅 + 옛 viewName 을 그 step
        // 의 view_name 으로 이전 (기존 단일 저장 동작과 일치하게).
        const anyHasFlag = migrated.some(s => s.save_as_view === true)
        if (!anyHasFlag) {
          for (let i = migrated.length - 1; i >= 0; i--) {
            if (isYamlKind(migrated[i].kind)) {
              migrated[i].save_as_view = true
              if (!migrated[i].view_name) migrated[i].view_name = loadedViewNameRaw || ''
              break
            }
          }
        }
        setSteps(migrated)
      } else {
        // 기존 SqlConverter 로 만든 doc — 빈 워크벤치로 시작 (Step 0 만 sql_text 로 채워짐)
        setSteps([])
      }

      // 로드 시점 View 정보 — 마지막 yaml_* step 의 YAML 또는 doc 의 semantic_yaml 로 결정
      // KBNL-103: step0 도 가장 앞선 step 으로 포함.
      const migrated = wbSteps.map(migrateStepShape)
      const lastYaml = findLastYamlStep([loadedStep0, ...migrated])
      const yamlForType = lastYaml?.result_yaml || data.semantic_yaml || ''
      const t = detectYamlType(yamlForType)
      setLoadedView(t
        ? { name: loadedViewNameRaw.trim() || (data.name || name), type: t }
        : null)

      const ownerHint = data.is_mine === false
        ? ` (작성자: ${data.owner_name || data.owner_user_id || '?'} — 저장 시 내 워크스페이스에 복사)`
        : ''
      showToast('success', `'${name}' 로드 완료${ownerHint}`)
    } catch (e) {
      showToast('error', `로드 실패: ${e.message}`)
    }
  }, [storageSettings, showToast, captureSnapshot, restoreSnapshot])

  // ── 새로 만들기 / 삭제 / 이름 변경 ─────────────────────────────────────
  // KBNL-99 §3: 새 작업 진입 시 기본 yaml_generate step 1개를 자동으로 추가해
  // 사용자가 SQL Converter 처럼 'SQL 입력 → 바로 변환' 흐름을 쓸 수 있게 한다.
  const handleNew = useCallback(() => {
    // KBNL-105: 떠나는 워크벤치 상태를 캐시에 스냅샷 후 새 세션 키 발급
    //   (진행 중 변환이 새 작업으로 덮어써지지 않게 — 완료되면 해당 캐시에 반영됨).
    projectCacheRef.current.set(wbKeyRef.current, captureSnapshot())
    newWbSeqRef.current += 1
    wbKeyRef.current = `new-${newWbSeqRef.current}`
    setSelectedQuery(null)
    setSelectedQueryIsMine(true)
    setSelectedQueryOwner('')
    setSelectedQueryTokenCount(null)
    setQueryName('')
    setQueryDesc('')
    setViewName('')
    setLoadedView(null)
    setSourceSql('')
    setHighlightOverride(null)
    // KBNL-103: Step 0 자체가 첫 변환 step (kind yaml_generate, SV 저장) 이므로
    // 별도 step 을 자동 생성하지 않는다. 추가 단계는 + step 버튼으로.
    setStep0({ ...makeStep({ kind: 'yaml_generate' }), save_as_view: true })
    setSteps([])
    setActiveStepKey('step-0')
  }, [captureSnapshot])

  const handleDelete = useCallback(async () => {
    if (!selectedQuery) return
    if (!selectedQueryIsMine) {
      showToast('error', '다른 사용자의 SQL 은 삭제할 수 없습니다.')
      return
    }
    if (!window.confirm(`'${selectedQuery}'을(를) 삭제할까요?`)) return
    try {
      await api.deleteSqlQuery(selectedQuery, storageSettings)
      // KBNL-117: 삭제된 워크벤치의 캐시 제거 — 동명 재생성 시 옛 스냅샷 복원 방지.
      projectCacheRef.current.delete(wbKeyRef.current)
      handleNew()
      await loadQueries()
      showToast('success', '삭제 완료')
    } catch (e) {
      showToast('error', `삭제 실패: ${e.message}`)
    }
  }, [selectedQuery, selectedQueryIsMine, storageSettings, showToast, loadQueries, handleNew])

  // KBNL-141(리뷰): renamingQuery 는 owner::name 키로 보관 — 같은 이름 다른 owner 행이
  // 동시에 rename 입력으로 바뀌던 문제 방지. rename 은 본인 항목에서만 시작됨.
  const handleStartRename = useCallback((q) => {
    setRenamingQuery(`${q.owner_user_id || ''}::${q.name}`)
    setRenameValue(q.name)
  }, [])
  const handleCancelRename = useCallback(() => { setRenamingQuery(null) }, [])
  const handleConfirmRename = useCallback(async () => {
    const renKey = renamingQuery
    if (!renKey) return
    const sep = renKey.indexOf('::')
    const oldOwner = sep >= 0 ? renKey.slice(0, sep) : ''
    const oldName = sep >= 0 ? renKey.slice(sep + 2) : renKey
    const newName = renameValue.trim()
    if (!oldName) return
    if (!newName || newName === oldName) { setRenamingQuery(null); return }
    const myQueries = savedQueries.filter(q => q.is_mine !== false)
    if (myQueries.some(q => q.name === newName)) {
      showToast('error', `이미 '${newName}' 이름의 SQL 이 존재합니다.`)
      setRenamingQuery(null)
      return
    }
    try {
      await api.renameSqlQuery(oldName, newName, storageSettings)
      // KBNL-141(리뷰): endsWith(::name) 는 같은 이름의 타인 캐시까지 지운다 → 정확한
      //   키만 한정 삭제. rename 은 본인 항목에서만 일어나므로, self 가 갖는 두 표기
      //   (uid 형 `${oldOwner}::name` + self-canonical `::name`, KBNL-106)만 무효화.
      //   타 owner 의 `otheruid::name` 은 매칭되지 않아 안전.
      const oldKeys = [...new Set([`${oldOwner}::${oldName}`, `::${oldName}`])]
      for (const k of oldKeys) projectCacheRef.current.delete(k)
      if (oldKeys.includes(wbKeyRef.current)) {
        wbKeyRef.current = wbKeyRef.current.slice(0, -oldName.length) + newName
      }
      if (selectedQuery === oldName && selectedQueryOwner === oldOwner) {
        setSelectedQuery(newName); setQueryName(newName)
      }
      await loadQueries()
      showToast('success', `'${oldName}' → '${newName}' 이름 변경 완료`)
    } catch (e) {
      showToast('error', `이름 변경 실패: ${e.message}`)
    } finally {
      setRenamingQuery(null)
    }
  }, [renamingQuery, renameValue, selectedQuery, selectedQueryOwner, savedQueries, storageSettings, showToast, loadQueries])

  // ── SQL 워크벤치 복제 (Duplicate) ────────────────────────────────────
  // workbench_steps 가 백엔드 (KBNL-187 이후) 에서 정상 저장/조회되므로 본문 spread
  // 만으로 step 들도 함께 복제된다.
  const openDuplicate = useCallback((q) => {
    const myQueries = (savedQueries || []).filter(x => x.is_mine !== false)
    setDuplicateDialog({
      sourceName: q.name,
      defaultName: suggestDuplicateName(q.name, myQueries),
    })
  }, [savedQueries])

  const handleDuplicate = useCallback(async (newName) => {
    if (!duplicateDialog) return
    const sourceName = duplicateDialog.sourceName
    if (!newName || !newName.trim()) { showToast('error', '이름을 입력하세요.'); return }
    const finalName = newName.trim()
    const myQueries = (savedQueries || []).filter(x => x.is_mine !== false)
    if (myQueries.some(q => q.name === finalName)) {
      showToast('error', `이미 '${finalName}' 이름의 SQL 이 존재합니다.`)
      return
    }
    setDuplicating(true)
    try {
      const src = await api.getSqlQuery(sourceName, storageSettings)
      const origViewName = (src.conversion_settings?.view_name || '').trim() || sourceName
      const viewNameMatchesSql = origViewName === sourceName
      const myMetricViews    = (savedMetricViews || []).filter(v => v.is_mine !== false)
      const myCompositeViews = (savedCompositeViews || []).filter(v => v.is_mine !== false)
      const dupViewName = viewNameMatchesSql
        ? finalName
        : suggestDuplicateName(origViewName, [...myMetricViews, ...myCompositeViews])

      await api.saveSqlQuery({
        ...src,
        name: finalName,
        conversion_settings: {
          ...(src.conversion_settings || {}),
          view_name: viewNameMatchesSql ? '' : dupViewName,
        },
      }, storageSettings)

      const linkedSV = myMetricViews.find(v => v.name === origViewName)
      const linkedCV = myCompositeViews.find(v => v.name === origViewName)
      const copiedExtras = []
      if (linkedSV) {
        try {
          const sv = await api.getView(origViewName, storageSettings)
          await api.saveView({ ...sv, name: dupViewName }, storageSettings)
          copiedExtras.push(`Semantic View '${dupViewName}'`)
        } catch (e) {
          showToast('warning', `Semantic View 복제 실패: ${e.message}`)
        }
      }
      if (linkedCV) {
        try {
          const cv = await api.getCompositeView(origViewName, storageSettings)
          await api.saveCompositeView({ ...cv, name: dupViewName }, storageSettings)
          copiedExtras.push(`Composite View '${dupViewName}'`)
        } catch (e) {
          showToast('warning', `Composite View 복제 실패: ${e.message}`)
        }
      }
      const extra = copiedExtras.length ? ` (+ ${copiedExtras.join(', ')})` : ''
      showToast('success', `'${sourceName}' → '${finalName}' 복제 완료${extra}`)
      setDuplicateDialog(null)
      await loadQueries()
    } catch (e) {
      showToast('error', `복제 실패: ${e.message}`)
    } finally {
      setDuplicating(false)
    }
  }, [duplicateDialog, savedQueries, savedMetricViews, savedCompositeViews, storageSettings, showToast, loadQueries])

  // ── Step 조작 ──────────────────────────────────────────────────────────

  const addStep = useCallback((kind) => {
    // KBNL-99: 소스가 단일 텍스트라 yaml 입력 step 도 어디서든 소스를 YAML 로 해석 →
    // 위치/순서 제약 없음.
    const newStep = makeStep({ kind })
    setSteps(prev => [...prev, newStep])
    setActiveStepKey(`step-${newStep.id}`)  // KBNL-99: 새로 추가된 step 으로 자동 이동
  }, [])

  // KBNL-99: step 사이 임의 위치에 삽입. insertAt = 새 step 의 인덱스 (0 = 맨 앞).
  const insertStep = useCallback((insertAt, kind) => {
    const newStep = makeStep({ kind })
    setSteps(prev => [...prev.slice(0, insertAt), newStep, ...prev.slice(insertAt)])
    setActiveStepKey(`step-${newStep.id}`)  // KBNL-99: 삽입된 step 으로 자동 이동
  }, [])

  const removeStep = useCallback((idx) => {
    setSteps(prev => {
      const target = prev[idx]
      if (!target) return prev
      // KBNL-99: 삭제 후 가상 체인에서 뒤 cumulative step 들의 입력 type 호환성 검사.
      // 삭제로 출력 chain 이 바뀌어 입력이 안 맞게 되는 뒤 step 의 결과를 리셋.
      const afterDelete = prev.filter((_, i) => i !== idx)
      const affected = []   // afterDelete 기준 인덱스
      // 삭제 위치(idx) 이후였던 step 들 = afterDelete 의 idx 부터
      for (let i = idx; i < afterDelete.length; i++) {
        const st = afterDelete[i]
        if (st.mode !== 'cumulative') continue
        const needIn = inputKindOf(st.kind)
        let satisfied = false
        for (let j = i - 1; j >= 0; j--) {
          if (outputKindOf(afterDelete[j].kind) === needIn) { satisfied = true; break }
        }
        if (!satisfied && needIn === 'sql') satisfied = true   // source 로 충족
        if (!satisfied) {
          const hasRes = (st.result_sql && st.result_sql.trim()) || (st.result_yaml && st.result_yaml.trim())
          if (hasRes || st.status !== 'idle') affected.push(i)
        }
      }
      // 단순히 base 가 바뀌는 (호환은 되지만 값이 달라지는) cumulative 뒤 step 도 안내.
      const anyCumulativeAfter = prev.slice(idx + 1).some(s => s.mode === 'cumulative')

      if (anyCumulativeAfter || affected.length > 0) {
        const tail = affected.length > 0
          ? ` 입력이 더 이상 맞지 않는 Step ${affected.map(i => i + 1).join(', ')} 의 결과가 초기화됩니다.`
          : ' 이후 cumulative step 의 base 가 변경됩니다.'
        if (!window.confirm(`Step ${idx + 1} 을 삭제합니다.${tail} 계속할까요?`)) {
          return prev
        }
      }

      const resetIds = new Set(affected)
      const next = afterDelete.map((st, i) => resetIds.has(i) ? {
        ...st,
        result_sql: '',
        result_yaml: '',
        base_sql: '',
        base_yaml: '',
        base_override: '',
        edited: false,
        status: 'idle',
        error: null,
      } : st)

      // 활성 step 이 삭제된 거면 인접 step (또는 step-0) 으로 이동.
      setActiveStepKey(currKey => {
        if (currKey === `step-${target.id}`) {
          const fallback = next[idx] || next[idx - 1] || null
          return fallback ? `step-${fallback.id}` : 'step-0'
        }
        return currKey
      })
      return next
    })
  }, [])

  const patchStep = useCallback((idx, patch) => {
    // KBNL-103: idx === -1 이면 Step 0(step0) 을 패치 → 모든 setStep* 핸들러가 그대로 재사용됨.
    if (idx === STEP0_IDX) { setStep0(prev => ({ ...prev, ...patch })); return }
    setSteps(prev => prev.map((s, i) => i === idx ? { ...s, ...patch } : s))
  }, [])

  // KBNL-105: runStep 결과 적용 — 시작 시점 워크벤치(runKey)가 아직 활성이면 라이브 state
  //   에, 전환됐으면 그 워크벤치의 캐시 스냅샷에만 반영(교차 오염 방지 + 결과 유실 방지).
  //   ★ patchStep 선언 뒤에 위치해야 함 — dep 배열 [patchStep] 이 렌더 중 평가되므로
  //     앞에 두면 use-before-init(TDZ). minify(terser) 가 우연히 가렸던 잠재 버그.
  const applyRunResult = useCallback((runKey, idx, patch) => {
    if (runKey === wbKeyRef.current) {
      patchStep(idx, patch)
      return true
    }
    const snap = projectCacheRef.current.get(runKey)
    if (!snap) return false
    snap.__dirty = true   // 복귀 시 서버 재로드 대신 이 스냅샷을 복원하도록 표시
    if (idx === STEP0_IDX) {
      snap.step0 = { ...snap.step0, ...patch }
    } else if (Array.isArray(snap.steps) && snap.steps[idx]) {
      snap.steps = snap.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    }
    return false
  }, [patchStep])

  const setStepPrompt = useCallback((idx, value) => patchStep(idx, { prompt: value }), [patchStep])
  const setStepMode   = useCallback((idx, mode)  => patchStep(idx, { mode }), [patchStep])
  // KBNL-120: yaml_to_sql 변환 엔진 (rule | llm) — 결과는 초기화하지 않음 (재실행 시 반영)
  const setStepEngine = useCallback((idx, engine) => patchStep(idx, { engine }), [patchStep])
  // KBNL-99: 생성 후 step 의 변환 종류(kind) 변경 — 입력/출력 type 이 달라지므로
  // 기존 결과/스냅샷을 비우고 status 리셋. 사용자에게 결과 손실 confirm.
  // cascade: 변경된 step 의 출력 type 이 바뀌면, 그 뒤의 cumulative chain 에서
  // 입력 type 이 안 맞는 step 들도 결과를 리셋(재실행 필요)해서 조용히 깨지지 않게 한다.
  const setStepKind = useCallback((idx, nextKind) => {
    // KBNL-103: Step 0(idx=-1) kind 변경 — 본인 결과 초기화 + 하위 cumulative cascade.
    if (idx === STEP0_IDX) {
      setStep0(prev0 => {
        if (prev0.kind === nextKind) return prev0
        const selfHadResult = (prev0.result_sql && prev0.result_sql.trim()) || (prev0.result_yaml && prev0.result_yaml.trim())
        // step0 출력 type 변경이 하위 cumulative chain 의 입력 충족에 영향을 주는지 시뮬레이션
        const sim0Kind = nextKind
        const affected = []
        for (let i = 0; i < steps.length; i++) {
          const st = steps[i]
          if (st.mode !== 'cumulative') continue
          const needIn = inputKindOf(st.kind)
          let satisfied = false
          for (let j = i - 1; j >= 0; j--) {
            if (outputKindOf(steps[j].kind) === needIn) { satisfied = true; break }
          }
          if (!satisfied && outputKindOf(sim0Kind) === needIn) satisfied = true
          if (!satisfied && needIn === 'sql') satisfied = true   // source(sourceSql) 로 충족
          if (!satisfied) {
            const hasRes = (st.result_sql && st.result_sql.trim()) || (st.result_yaml && st.result_yaml.trim())
            if (hasRes || st.status !== 'idle') affected.push(i)
          }
        }
        if (selfHadResult || affected.length > 0) {
          const tail = affected.length > 0
            ? ` 또한 이어지는 Step ${affected.map(i => i + 1).join(', ')} 의 입력이 더 이상 맞지 않아 결과가 초기화됩니다.`
            : ''
          if (!window.confirm(`Step 0 의 변환 종류를 바꾸면 기존 결과가 초기화됩니다.${tail} 계속할까요?`)) {
            return prev0
          }
        }
        if (affected.length > 0) {
          const resetIds = new Set(affected)
          setSteps(prevSteps => prevSteps.map((st, i) => resetIds.has(i) ? {
            ...st, result_sql: '', result_yaml: '', base_sql: '', base_yaml: '',
            base_override: '', edited: false, status: 'idle', error: null,
          } : st))
        }
        return { ...prev0, kind: nextKind, result_sql: '', result_yaml: '', base_sql: '', base_yaml: '', edited: false, status: 'idle', error: null }
      })
      return
    }
    setSteps(prev => {
      const s = prev[idx]
      if (!s || s.kind === nextKind) return prev

      // 변경 step 본인 결과 손실 여부
      const selfHadResult = (s.result_sql && s.result_sql.trim()) || (s.result_yaml && s.result_yaml.trim())

      // cascade: idx 이후 cumulative step 들의 입력 type 호환성 시뮬레이션.
      // 새 kind 를 반영한 가상 chain 에서 각 step 의 base 출력 type 을 따라가며,
      // 입력 type 이 안 맞고 결과가 있는 step 을 affected 로 모은다.
      const simulated = prev.map((st, i) => i === idx ? { ...st, kind: nextKind } : st)
      const affected = []   // 뒤 step 중 리셋 대상 인덱스
      for (let i = idx + 1; i < simulated.length; i++) {
        const st = simulated[i]
        if (st.mode !== 'cumulative') continue   // independent 는 source 기반이라 영향 없음
        // 이 step 의 입력 type 이 직전 cumulative chain 에서 실제로 충족되는지:
        // 가장 가까운 직전 step 중 같은 출력 type 을 내는 게 있는지 본다.
        const needIn = inputKindOf(st.kind)
        let satisfied = false
        for (let j = i - 1; j >= 0; j--) {
          if (outputKindOf(simulated[j].kind) === needIn) { satisfied = true; break }
        }
        // source(step0) 도 후보 — sql 입력은 항상 소스로 충족 가능
        if (!satisfied && needIn === 'sql') satisfied = true
        if (!satisfied) {
          const hasRes = (st.result_sql && st.result_sql.trim()) || (st.result_yaml && st.result_yaml.trim())
          if (hasRes || st.status !== 'idle') affected.push(i)
        }
      }

      // confirm — 손실 범위 안내
      if (selfHadResult || affected.length > 0) {
        const tail = affected.length > 0
          ? ` 또한 이어지는 Step ${affected.map(i => i + 1).join(', ')} 의 입력이 더 이상 맞지 않아 결과가 초기화됩니다.`
          : ''
        if (!window.confirm(`변환 종류를 바꾸면 이 step 의 기존 결과가 초기화됩니다.${tail} 계속할까요?`)) {
          return prev
        }
      }

      const resetIds = new Set([idx, ...affected])
      return prev.map((st, i) => resetIds.has(i) ? {
        ...st,
        kind: i === idx ? nextKind : st.kind,   // 본인만 kind 변경, 뒤는 결과만 리셋
        result_sql: '',
        result_yaml: '',
        base_sql: '',
        base_yaml: '',
        base_override: '',
        edited: false,
        status: 'idle',
        error: null,
      } : st)
    })
  }, [steps])
  const setStepResultSql  = useCallback((idx, value) => patchStep(idx, { result_sql: value, edited: true }), [patchStep])
  const setStepResultYaml = useCallback((idx, value) => patchStep(idx, { result_yaml: value, edited: true }), [patchStep])
  // KBNL-99: step 별 SV/CV 저장 토글 + 이름
  const setStepSaveAsView = useCallback((idx, value) => patchStep(idx, { save_as_view: !!value }), [patchStep])
  const setStepViewName   = useCallback((idx, value) => patchStep(idx, { view_name: value }), [patchStep])
  // KBNL-99: 좌측 base 패널의 사용자 직접 편집 / import / upload (모든 step)
  const setStepBaseOverride = useCallback((idx, value) => patchStep(idx, { base_override: value }), [patchStep])

  // ── KBNL-101: step 결과의 존재하지 않는 테이블/컬럼 검증 ───────────────
  const validateStep = useCallback(async (step) => {
    const isYaml = isYamlKind(step.kind)
    const content = isYaml ? step.result_yaml : step.result_sql
    if (!content || !content.trim()) {
      showToast('warning', '검증할 결과가 없습니다. 먼저 step 을 실행하세요.')
      return
    }
    if (!settings?.project_id) {
      showToast('error', '설정에서 Project ID 를 입력하세요.')
      return
    }
    // KBNL-101 fix: dataset 하나만이 아니라 selected_tables 의 모든 dataset 을
    // 후보로 넘김 (table 명만 있는 KB 표준 source 가 여러 dataset 에 흩어질 수 있음).
    const dsCandidates = [
      ...(settings.dataset_id ? [settings.dataset_id] : []),
      ...Object.keys(settings.selected_tables || {}),
    ]
    const dataset = Array.from(new Set(dsCandidates)).join(',')
    setValidateState(prev => ({ ...prev, [step.id]: { running: true, result: null } }))
    try {
      const res = isYaml
        ? await api.validateSemantic({ semantic_yaml: content, project_id: settings.project_id, dataset_id: dataset })
        : await api.validateSql({ sql_text: content, project_id: settings.project_id, dataset_id: dataset })
      setValidateState(prev => ({ ...prev, [step.id]: { running: false, result: res } }))
      if (res.valid) {
        showToast('success', `검증 통과 — 존재하지 않는 테이블/컬럼 없음 (${res.method})`)
      } else {
        const n = (res.unknown_tables?.length || 0) + (res.unknown_columns?.length || 0) + (res.errors?.length || 0)
        showToast('warning', `검증 실패 — 문제 ${n}건 발견 (${res.method})`)
      }
    } catch (e) {
      setValidateState(prev => ({ ...prev, [step.id]: { running: false, result: { valid: false, errors: [e.message], method: 'error' } } }))
      showToast('error', `검증 실패: ${e.message}`)
    }
  }, [settings, showToast])

  // KBNL-136(feedback 20260618): 검증 결과 패널 닫기 — result 만 비워 패널 제거.
  const dismissValidation = useCallback((stepId) => {
    setValidateState(prev => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), result: null } }))
  }, [])

  // ── Step 실행 (LLM 호출) — kind 별 분기 ───────────────────────────────
  const runStep = useCallback(async (idx) => {
    // KBNL-103: idx === -1 이면 Step 0(step0). 그 외엔 steps[idx].
    const isStep0 = idx === STEP0_IDX
    const step = isStep0 ? step0 : steps[idx]
    if (!step) return
    // KBNL-105: 이 변환이 속한 워크벤치 키를 시작 시점에 캡처. await 도중 사용자가
    //   다른 프로젝트로 전환해도, 완료 결과는 이 키(원래 워크벤치)로 라우팅된다.
    const runKey = wbKeyRef.current
    const stepLabel = isStep0 ? 'Step 0' : `Step ${idx + 1}`
    // KBNL-99 §5: 사용자 프롬프트는 '추가 지시' 로 취급. 비어 있어도 기본
    // 시스템/사용자 프롬프트 템플릿만으로 실행 가능.
    if (!settings?.project_id) {
      showToast('error', '설정에서 Project ID를 입력하세요.')
      return
    }
    // KBNL-103: Step 0 의 base(변환 전)는 항상 sourceSql(상위 step 없음). kind 에 따라
    // SQL/YAML 로 해석. 그 외 step 은 기존 resolveBase + base_override.
    let base
    if (isStep0) {
      base = { kind: inputKindOf(step.kind), value: sourceSql }
    } else {
      base = resolveBase(sourceSql, steps, idx, step.kind, step.mode, step0)
      // KBNL-99: 사용자가 좌측 base 패널을 직접 편집/import/upload 한 경우 그 값 우선
      // (SQL/YAML 입력 모두). base.kind 는 유지.
      if (step.base_override && step.base_override.trim()) {
        base = { kind: base.kind, value: step.base_override }
      }
    }
    if (!base.value || !base.value.trim()) {
      const msg = (step.kind === 'yaml_refine' || step.kind === 'yaml_to_sql')
        ? '이전 YAML 결과가 비어 있습니다. yaml_generate step 을 먼저 실행하거나 좌측에 YAML 을 직접 입력하세요.'
        : 'base SQL 이 비었습니다 — 소스 SQL 또는 직전 step 의 결과를 먼저 채우세요.'
      showToast('warning', msg)
      return
    }

    // KBNL-106: 저장된 워크벤치면 실행 리스 획득 — 같은 워크벤치를 다른 창에서
    //   동시에 변환하는 것 차단 + 타 창에 '변환 중' 표시. 미저장(new-N)은 미적용.
    //   advisory — 리스 백엔드 오류는 변환을 막지 않음.
    const leaseIdent = leaseIdentityOf(runKey)
    let leaseHeld = false
    if (leaseIdent) {
      try {
        const r = await api.wbRunLeaseAcquire({
          name: leaseIdent.name, owner_user_id: leaseIdent.owner,
          holder_id: tabIdRef.current, step_label: `${stepLabel} — ${labelForKind(step.kind)}`,
        })
        if (r && r.acquired === false) {
          const h = r.holder || {}
          showToast('warning', `다른 창에서 이 워크벤치를 변환 중입니다${h.user_name ? ` (${h.user_name}${h.step_label ? ` · ${h.step_label}` : ''})` : ''}. 완료 후 다시 시도하세요.`)
          return
        }
        const lk = `${leaseIdent.owner}::${leaseIdent.name}`
        leaseCountRef.current.set(lk, (leaseCountRef.current.get(lk) || 0) + 1)
        ensureLeaseHeartbeat()
        leaseHeld = true
      } catch { /* advisory — 리스 오류 무시 */ }
    }

    // KBNL-126: 잡 미러 — 저장된 워크벤치 + LLM 호출 step 만 (규칙 엔진 yaml_to_sql 은
    //   즉시 끝나므로 제외). job_id 는 클라가 생성 — 응답 유실에도 폴링/회수 가능.
    const usesLlm = step.kind !== 'yaml_to_sql' || step.engine === 'llm'
    let jobId = null
    let jobPayload = null
    if (leaseIdent && usesLlm) {
      jobId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `j${Date.now()}${Math.random().toString(36).slice(2)}`
      jobPayload = {
        job_id: jobId,
        owner_user_id: leaseIdent.owner,
        name: leaseIdent.name,
        step: {
          id: step.id, no: isStep0 ? 0 : idx + 1,
          kind: step.kind, engine: step.engine || '', prompt: step.prompt || '',
        },
      }
    }

    // status: running + base 스냅샷 (kind 에 따라 sql/yaml 슬롯)
    // KBNL-141: 강제 중단용 AbortController — jobId 가 있는(미러되는 LLM) 실행만 등록.
    const controller = new AbortController()
    if (jobId) runCtrlRef.current.set(jobId, controller)
    const reqOpts = { signal: controller.signal }
    patchStep(idx, {
      status: 'running',
      error: null,
      base_sql:  base.kind === 'sql'  ? base.value : '',
      base_yaml: base.kind === 'yaml' ? base.value : '',
    })

    try {
      if (step.kind === 'sql_refine') {
        // SQL → SQL 정제 — /api/sql-workbench/refine
        const res = await api.sqlWorkbenchRefine({
          base_sql: base.value,
          job: jobPayload,
          prompt: step.prompt,
          project_id: settings.project_id,
          location: settings.vertex_location || settings.location || APP_DEFAULTS.location,
          model_name: settings.model_name || APP_DEFAULTS.model_name,
          glossary_id: settings.glossary_id || '',
          glossary_ids: Array.isArray(settings.glossary_ids) ? settings.glossary_ids : [],
          user_id: storageSettings?.user_id || '',
        }, reqOpts)
        const live = applyRunResult(runKey, idx, {
          result_sql: res.refined_sql || '',
          edited: false,
          status: 'done',
          updated_at: new Date().toISOString(),
        })
        showToast('success', `${live ? '' : '[다른 워크벤치] '}${stepLabel} SQL 정제 완료`)
      } else if (step.kind === 'yaml_generate') {
        // SQL → YAML 변환 — 기존 /api/sql-to-semantic
        const res = await api.sqlToSemantic({
          sql_text: base.value,
          job: jobPayload,
          project_id: settings.project_id,
          location: settings.vertex_location || settings.location || APP_DEFAULTS.location,
          model_name: settings.model_name || APP_DEFAULTS.model_name,
          glossary_id: settings.glossary_id || '',
          glossary_ids: Array.isArray(settings.glossary_ids) ? settings.glossary_ids : [],
          reference_views: [],
          // KBNL-103: Step 0 는 상위 step 이 없어 누적 prior 가 없음.
          user_requirements: isStep0 ? '' : buildPriorIntents({ steps, idx }),
          storage_type: storageSettings?.storage_type || APP_DEFAULTS.storage_type,
          gcs_path:     storageSettings?.gcs_path    || '',
          fs_project:   storageSettings?.fs_project  || '',
          fs_database:  storageSettings?.fs_database || '',
          fs_collection:storageSettings?.fs_collection || '',
          user_id:      storageSettings?.user_id     || '',
        }, reqOpts)
        const yamlOut = res.semantic_yaml || ''
        const live = applyRunResult(runKey, idx, {
          result_yaml: yamlOut,
          edited: false,
          status: 'done',
          updated_at: new Date().toISOString(),
        })
        const fixMsg = res.fix_attempts > 0 ? ` (자동 수정 ${res.fix_attempts}회)` : ''
        const detected = detectYamlType(yamlOut)
        const typeMsg = detected ? ` → ${typeLabel(detected)}` : ''
        showToast('success', `${live ? '' : '[다른 워크벤치] '}${stepLabel} YAML 생성 완료${fixMsg}${typeMsg}`)
      } else if (step.kind === 'yaml_refine') {
        // YAML → YAML 정제 — /api/sql-workbench/refine-yaml (KBNL-66)
        const res = await api.sqlWorkbenchRefineYaml({
          base_yaml: base.value,
          job: jobPayload,
          prompt: step.prompt,
          project_id: settings.project_id,
          location: settings.vertex_location || settings.location || APP_DEFAULTS.location,
          model_name: settings.model_name || APP_DEFAULTS.model_name,
          glossary_id: settings.glossary_id || '',
          glossary_ids: Array.isArray(settings.glossary_ids) ? settings.glossary_ids : [],
          user_id: storageSettings?.user_id || '',
        }, reqOpts)
        const yamlOut = res.refined_yaml || ''
        const live = applyRunResult(runKey, idx, {
          result_yaml: yamlOut,
          edited: false,
          status: 'done',
          updated_at: new Date().toISOString(),
        })
        const detected = detectYamlType(yamlOut)
        const typeMsg = detected ? ` → ${typeLabel(detected)}` : ''
        showToast('success', `${live ? '' : '[다른 워크벤치] '}${stepLabel} YAML 정제 완료${typeMsg}`)
      } else if (step.kind === 'yaml_to_sql') {
        // KBNL-99/120: YAML → SQL — 엔진 선택.
        //   rule(기본): /api/convert-semantic — 결정적 규칙 변환 (정합성·재현성, LLM 없음)
        //   llm:        /api/sql-workbench/yaml-to-sql — LLM 1회 호출 (유연성·가독성·지시 반영)
        let sqlOut
        let engineLabel
        if (step.engine === 'llm') {
          const res = await api.sqlWorkbenchYamlToSql({
            base_yaml: base.value,
            job: jobPayload,
            prompt: step.prompt || '',
            project_id: settings.project_id,
            location: settings.vertex_location || settings.location || APP_DEFAULTS.location,
            model_name: settings.model_name || APP_DEFAULTS.model_name,
            glossary_id: settings.glossary_id || '',
            glossary_ids: Array.isArray(settings.glossary_ids) ? settings.glossary_ids : [],
            user_id: storageSettings?.user_id || '',
          }, reqOpts)
          sqlOut = (res.sql || '').trim()
          engineLabel = 'LLM'
        } else {
          const res = await api.convertSemantic({
            semantic_yaml: base.value,
            project_id: settings.project_id || 'my-project',
            dataset_id: settings.dataset_id || (Object.keys(settings.selected_tables || {})[0] || 'my_dataset'),
            view_name: queryName || 'workbench_view',
            description: step.prompt || '',
            catalog: 'my_catalog',
            schema_name: 'my_schema',
          }, reqOpts)
          sqlOut = (res.bq_view || res.bq_mv || res.ddl || '').trim()
          engineLabel = '규칙'
        }
        const live = applyRunResult(runKey, idx, {
          result_sql: sqlOut,
          edited: false,
          status: sqlOut ? 'done' : 'error',
          error: sqlOut ? null : 'SQL 생성 결과가 비었습니다 (입력 YAML 형식 확인).',
          updated_at: new Date().toISOString(),
        })
        const pfx = live ? '' : '[다른 워크벤치] '
        if (sqlOut) {
          showToast('success', `${pfx}${stepLabel} YAML → SQL 변환 완료 (${engineLabel})`)
        } else {
          showToast('warning', `${pfx}${stepLabel} YAML → SQL: 빈 결과 — 입력 YAML 형식 확인`)
        }
      }
      // KBNL-126: 응답을 정상 수신했으면 잡 회수(삭제) — 미회수 배너 중복 방지.
      //   (에러/응답 유실 시엔 doc 이 남아 배너로 회수·재실행·정리 가능)
      if (jobId) api.wbJobsCollect(jobId).catch(() => {})
    } catch (e) {
      // KBNL-141: 사용자가 [중단] → fetch abort. 결과/에러를 적용하지 않고 조용히 종료
      //   (중단은 cancelJob 이 잡 정리·상태 해제까지 처리).
      if (e?.name === 'AbortError') {
        return
      }
      const live = applyRunResult(runKey, idx, { status: 'error', error: e.message })
      showToast('error', `${live ? '' : '[다른 워크벤치] '}${stepLabel} 실행 실패: ${e.message}`)
    } finally {
      if (jobId) runCtrlRef.current.delete(jobId)
      // KBNL-106: 리스 해제 (같은 워크벤치의 마지막 로컬 실행이 끝났을 때만 서버 해제)
      if (leaseHeld && leaseIdent) releaseLease(leaseIdent)
    }
  }, [steps, step0, sourceSql, queryName, settings, storageSettings, showToast, patchStep, applyRunResult])

  // ── KBNL-126: 서버측 잡 회수/정리/재실행 ───────────────────────────────
  // job.step.id 로 현재 워크벤치의 step 을 찾는다 (step0 포함). 없으면 -2.
  const findJobStepIdx = useCallback((job) => {
    const sid = String(job?.step?.id ?? '')
    if (!sid) return -2
    if (String(step0.id) === sid) return STEP0_IDX
    const i = steps.findIndex(s => String(s.id) === sid)
    return i >= 0 ? i : -2
  }, [step0, steps])

  const removeJobLocal = useCallback((jobId) => {
    setWbJobs(prev => prev.filter(j => j.job_id !== jobId))
    api.wbJobsCollect(jobId).catch(() => {})
  }, [])

  // [가져오기] — done 잡의 결과를 해당 step 에 주입 후 회수.
  const collectJob = useCallback((job) => {
    const idx = findJobStepIdx(job)
    if (idx === -2) {
      showToast('warning', '결과를 넣을 step 을 찾지 못했습니다 (step 삭제/변경됨). 필요 없으면 [버리기] 하세요.')
      return
    }
    const isYamlResult = !!(job.result_yaml || '').trim()
    patchStep(idx, {
      ...(isYamlResult ? { result_yaml: job.result_yaml } : { result_sql: job.result_sql }),
      edited: false,
      status: 'done',
      error: null,
      updated_at: new Date().toISOString(),
    })
    removeJobLocal(job.job_id)
    showToast('success', `이전 변환 결과를 Step ${job?.step?.no ?? '?'} 에 가져왔습니다.`)
  }, [findJobStepIdx, patchStep, removeJobLocal, showToast])

  // KBNL-141: [중단] — 진행 중 잡 강제 중단(서버 태스크 취소 + 정리), UI 즉시 해제.
  const cancelJob = useCallback((job) => {
    if (!window.confirm('진행 중인 변환을 중단할까요? 결과는 폐기됩니다.')) return
    // KBNL-141(리뷰): 클라 fetch 를 abort — 응답이 돌아와도 결과 적용 안 됨(요청 유지형).
    const ctrl = runCtrlRef.current.get(job.job_id)
    if (ctrl) { ctrl.abort(); runCtrlRef.current.delete(job.job_id) }
    setWbJobs(prev => prev.filter(j => j.job_id !== job.job_id))
    // 로컬 step 스피너도 해제 (해당 step 이 running 이면 idle 로)
    const idx = findJobStepIdx(job)
    if (idx === STEP0_IDX) { if (step0.status === 'running') patchStep(STEP0_IDX, { status: 'idle' }) }
    else if (idx >= 0) { patchStep(idx, { status: 'idle' }) }
    api.wbJobsCancel(job.job_id).then(() => showToast('info', '변환을 중단했습니다.'))
      .catch(e => showToast('error', `중단 실패: ${e.message}`))
  }, [findJobStepIdx, step0.status, patchStep, showToast])

  // [다시 실행] — 잡 정리 후 해당 step 재실행 (현재 입력 기준).
  const retryJob = useCallback((job) => {
    const idx = findJobStepIdx(job)
    removeJobLocal(job.job_id)
    if (idx === -2) {
      showToast('warning', '재실행할 step 을 찾지 못했습니다 (step 삭제/변경됨).')
      return
    }
    runStep(idx)
  }, [findJobStepIdx, removeJobLocal, runStep, showToast])

  // ── 저장 ───────────────────────────────────────────────────────────────
  // 직렬화 — 휘발성 필드(status, error)는 정리해서 저장. KBNL-66: kind axis.
  const serializeStep = (s) => ({
    id: s.id,
    kind: s.kind,
    mode: s.mode,
    prompt: s.prompt,
    base_sql: s.base_sql,
    base_yaml: s.base_yaml,
    result_sql: s.result_sql,
    result_yaml: s.result_yaml,
    edited: s.edited,
    updated_at: s.updated_at || new Date().toISOString(),
    // KBNL-99: step 별 SV/CV 저장 옵션
    save_as_view: !!s.save_as_view,
    view_name: s.view_name || '',
    base_override: s.base_override || '',
    // KBNL-120: yaml_to_sql 엔진 (rule | llm)
    engine: s.engine === 'llm' ? 'llm' : 'rule',
  })

  // 마지막 yaml_* step 의 YAML = "저장될 SV/CV". 사용자가 yaml_refine 으로 누적한 끝 결과.
  // KBNL-103: step0 도 가장 앞선 step 으로 포함해 "마지막 yaml" 판정.
  const finalStep = useMemo(() => findLastYamlStep([step0, ...steps]), [step0, steps])
  const finalYaml = finalStep?.result_yaml || ''
  const finalYamlType = useMemo(() => detectYamlType(finalYaml), [finalYaml])

  // KBNL-99: 소스칸 하이라이트 타입 — override 우선, 없으면 첫 step 의 입력 타입,
  // step 도 없으면 'sql'. ('sql' | 'yaml')
  // KBNL-103: 소스 하이라이트 = override 우선, 없으면 Step 0 의 입력 타입.
  const sourceHighlight = highlightOverride || inputKindOf(step0.kind)

  // ── KBNL-74 후속: 추천 모달 열기 (yaml 종류 분기) ──────────────────
  //   composite → picker 표시 (key 별로 식별) / single → 변환 결과 SV 기반 query
  // 모든 picker trigger (top + per-step) 가 공유.
  const openRecommendForYaml = useCallback((yamlText, yamlTypeLocal, key) => {
    if (yamlTypeLocal === 'composite') {
      setRecommendPickerKey(key)
      return
    }
    if (yamlTypeLocal === 'single' && yamlText.trim()) {
      const sums = extractViewSummariesFromYaml(yamlText)
      const s = sums[0]
      if (s) {
        setRecommendSeed({ name: s.name, description: s.description, yaml: s.yaml })
        setRecommendOpen(true)
        return
      }
    }
    // unknown — 그래도 모달은 열어 사용자 입력 받기 (mode='sql', 빈 prefill)
    setRecommendSeed(null)
    setRecommendOpen(true)
  }, [])

  // picker 외부 클릭 시 닫기 — 활성 picker 의 DOM 만 contains 체크
  useEffect(() => {
    if (!recommendPickerKey) return
    const onDocClick = (e) => {
      const el = pickerRefs.current[recommendPickerKey]
      if (el && !el.contains(e.target)) {
        setRecommendPickerKey(null)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [recommendPickerKey])

  /* "🔍 유사 SV 찾기 [▾]" 버튼 + composite picker 드롭다운 렌더 헬퍼.
   * Workbench 의 여러 위치(View 이름 행 / 각 yaml step 의 result-head) 에서 같은
   * UI 가 반복되므로 한 곳에 모음.
   *  - yamlText : 검색 기준 YAML 본문 (단일 SV YAML 또는 composite YAML)
   *  - yamlTypeLocal: detectYamlType 결과 ('single' | 'composite' | 'unknown' | null)
   *  - key      : picker 식별자 ('top' | 'step-N')
   *  - extraStyle: wrapper div 스타일 override (alignRight 등)
   *  - btnSize  : 'normal' (btn-secondary) | 'xs' (btn-xs, step 안 컴팩트용)
   */
  const renderRecommendPicker = (yamlText, yamlTypeLocal, key, extraStyle = {}, btnSize = 'normal') => {
    const isComposite = yamlTypeLocal === 'composite'
    const open = recommendPickerKey === key && isComposite
    const btnClass = btnSize === 'xs' ? 'btn-xs' : 'btn-secondary'
    const fontSize = btnSize === 'xs' ? 11 : undefined
    return (
      <div
        ref={el => {
          if (el) pickerRefs.current[key] = el
          else delete pickerRefs.current[key]
        }}
        style={{ position: 'relative', display: 'inline-block', ...extraStyle }}
      >
        <button
          className={btnClass}
          style={fontSize ? { fontSize } : undefined}
          onClick={() => openRecommendForYaml(yamlText, yamlTypeLocal, key)}
          title={
            isComposite
              ? '변환 결과의 sub-SV 별로 유사 SV 찾기'
              : '변환된 Semantic View 와 유사한 기존 SV 찾기'
          }
        >
          🔍 유사 SV 찾기{isComposite ? ' ▾' : ''}
        </button>
        {open && (
          <ul className="sv-recommend-picker">
            {extractViewSummariesFromYaml(yamlText).map(s => (
              <li
                key={s.name}
                onClick={() => {
                  // sub-SV 만 잘라낸 YAML 로 seed 검색 (composite 전체가 아니라)
                  setRecommendSeed({ name: s.name, description: s.description, yaml: s.yaml })
                  setRecommendPickerKey(null)
                  setRecommendOpen(true)
                }}
                title={`'${s.name}' 와 유사한 SV 찾기`}
              >
                <strong>{s.name}</strong>
                {s.description && (
                  <span className="picker-desc">— {s.description.slice(0, 40)}</span>
                )}
              </li>
            ))}
            {extractViewSummariesFromYaml(yamlText).length === 0 && (
              <li className="picker-empty">sub-SV 를 찾지 못했습니다.</li>
            )}
          </ul>
        )}
      </div>
    )
  }

  const proceedSave = useCallback(async () => {
    const name = queryName.trim()
    const vName = viewName.trim() || name
    const namesDiffer = vName !== name

    setSaving(true)

    const savedTo = []
    const failedAt = []

    try {
      // 1) SQL Query 스토리지 — 워크벤치 본체
      const sqlSaveRes = await api.saveSqlQuery({
        name,
        description: queryDesc,
        sql_text: sourceSql,                   // = Step 0
        semantic_yaml: finalYaml,              // = 최종 step 의 YAML (없으면 빈 문자열)
        conversion_settings: {
          // 워크벤치는 step 별 prompt 를 사용 — 여기엔 마지막 step 의 prompt 만 보존
          user_requirements: finalStep?.prompt || '',
          model_name: settings?.model_name || APP_DEFAULTS.model_name,
          glossary_id: settings?.glossary_id || '',
          glossary_ids: Array.isArray(settings?.glossary_ids) ? settings.glossary_ids : [],
          result_type: finalYamlType,
          view_name: viewName.trim(),
          source: 'workbench',
        },
        workbench_steps: steps.map(serializeStep),
        // KBNL-103: Step 0 의 변환 설정/결과 (가산적 필드 — 옛 doc 호환 유지)
        workbench_step0: serializeStep(step0),
      }, storageSettings)
      savedTo.push(namesDiffer ? `SQL Query '${name}'` : 'SQL Query')
      // KBNL-69: 저장 응답의 token_count 로 헤더 갱신
      setSelectedQueryTokenCount(sqlSaveRes?.token_count ?? null)

      // KBNL-110 정리: step 결과의 별도 SV/CV '자동 저장'은 제거. 각 패널의 💾 저장으로
      //   명시 저장하도록 변경했으므로, 워크벤치 저장은 프로젝트(sql_queries) 저장만 수행.

      // KBNL-141(리뷰): 저장 후 캐시 키(wbKeyRef)와 화면 선택 owner 를 같은 표기로 통일.
      //   기존엔 wbKeyRef 를 self-canonical `::name` 으로만 승격하고 선택 owner 는 실제
      //   uid 로 설정해, 저장→추가편집→타항목→재진입 시 떠날 때 snapshot(`::name`)과
      //   돌아올 때 조회 키(`uid::name`)가 어긋나 서버 저장본이 다시 로드(편집분 유실)됐다.
      const savedOwner = sqlSaveRes?.owner_user_id || storageSettings?.user_id || ''
      wbKeyRef.current = `${savedOwner}::${name}`

      setSelectedQuery(name)
      setSelectedQueryIsMine(true)
      setSelectedQueryOwner(savedOwner)
      await loadQueries()

      if (failedAt.length > 0) {
        const okList = savedTo.join(', ')
        const failList = failedAt.map(f => `${f.where}: ${f.message}`).join(' / ')
        showToast('warning', `일부 저장 실패 — 성공 [${okList}] · 실패 [${failList}]`)
      } else {
        const list = savedTo.join(' + ')
        showToast('success', `저장 완료 (${list})`)
      }
    } catch (e) {
      showToast('error', `저장 실패: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }, [queryName, queryDesc, viewName, sourceSql, step0, steps, finalYaml, finalStep, finalYamlType, settings, storageSettings, showToast, loadQueries])

  const handleSave = useCallback(() => {
    const name = queryName.trim()
    if (!name) { showToast('error', '워크벤치 이름을 입력하세요.'); return }
    // KBNL-99: 소스는 단일 텍스트.
    if (!sourceSql.trim()) { showToast('error', '소스를 입력하세요.'); return }
    // KBNL-99: 단일 viewName 헤더 필드 의존 제거 — step 별 view_name 으로 분산.

    // 동명 충돌 검사 — 워크벤치(SQL Query) 이름만. (step 별 SV/CV 자동 저장 제거됨 — KBNL-110)
    const overwrites = []
    const myQueries = savedQueries.filter(q => q.is_mine !== false)
    const isMyOwnUpdate = selectedQueryIsMine && name === selectedQuery
    if (myQueries.some(q => q.name === name) && !isMyOwnUpdate) {
      overwrites.push(`SQL Query '${name}'`)
    }

    if (overwrites.length > 0) {
      setOverwriteDialog({ overwrites })
      return
    }
    proceedSave()
  }, [queryName, sourceSql, step0, steps, savedQueries, selectedQuery, selectedQueryIsMine, savedMetricViews, savedCompositeViews, showToast, proceedSave])

  const confirmOverwrite = useCallback(() => {
    setOverwriteDialog(null)
    proceedSave()
  }, [proceedSave])

  // ── KBNL-108: 패널 내용을 type 에 맞게 Firestore 에 저장 (SQL / SV / CV) ──
  // 패널의 isYaml 여부 + detectYamlType 으로 kind 결정 후 이름 팝업을 띄운다.
  const openTypeSave = useCallback((content, isYaml) => {
    const text = (content || '').trim()
    if (!text) { showToast('warning', '저장할 내용이 없습니다.'); return }
    const kind = isYaml ? (detectYamlType(text) === 'composite' ? 'cv' : 'sv') : 'sql'
    const base = (queryName || 'workbench').trim() || 'workbench'
    const suffix = kind === 'cv' ? '_cv' : kind === 'sv' ? '_sv' : '_sql'
    setSaveAsDialog({ kind, content: text, defaultName: `${base}${suffix}` })
  }, [queryName, showToast])

  // 이름 확정 → 동명 충돌 시 confirm(덮어쓰기/취소-이름변경) → 저장.
  const doTypeSave = useCallback(async (rawName) => {
    const dlg = saveAsDialog
    if (!dlg) return
    const nm = (rawName || '').trim()
    if (!nm) return
    const list = dlg.kind === 'cv' ? savedCompositeViews : dlg.kind === 'sv' ? savedMetricViews : savedQueries
    const exists = (list || []).some(v => (v?.name ?? v) === nm)
    const label = dlg.kind === 'cv' ? 'Composite View' : dlg.kind === 'sv' ? 'Semantic View' : 'SQL'
    if (exists && !window.confirm(`'${nm}' (${label})이(가) 이미 존재합니다.\n\n[확인] 덮어쓰기 / [취소] 이름 변경`)) {
      return  // 팝업 유지 → 사용자가 이름 변경
    }
    setSaveAsBusy(true)
    try {
      if (dlg.kind === 'sv') {
        await api.saveView({ name: nm, description: queryDesc || '', semantic_yaml: dlg.content, sql_text: sourceSql }, storageSettings)
        setSavedMetricViews(prev => prev.some(v => (v?.name ?? v) === nm) ? prev : [...prev, { name: nm }])
      } else if (dlg.kind === 'cv') {
        await api.saveCompositeView({ name: nm, description: queryDesc || '', composite_yaml: dlg.content, selected_views: [], sql_text: sourceSql, sources: [] }, storageSettings)
        setSavedCompositeViews(prev => prev.some(v => (v?.name ?? v) === nm) ? prev : [...prev, { name: nm }])
      } else {
        await api.saveSqlQuery({ name: nm, description: queryDesc || '', sql_text: dlg.content }, storageSettings)
        loadQueries()
      }
      showToast('success', `${label} '${nm}' 저장 완료`)
      setSaveAsDialog(null)
    } catch (e) {
      showToast('error', `저장 실패: ${e.message}`)
    } finally {
      setSaveAsBusy(false)
    }
  }, [saveAsDialog, savedCompositeViews, savedMetricViews, savedQueries, queryDesc, sourceSql, storageSettings, showToast, loadQueries])

  // ── KBNL-109: 워크벤치 통째 내보내기/가져오기 ─────────────────────────
  // 프로젝트 전체(소스 + step0 + steps + 설정)를 단일 JSON 으로 직렬화.
  const buildWorkbenchExportJson = useCallback(() => {
    const lastYaml = findLastYamlStep([step0, ...steps])
    const payload = {
      schema_version: 1,
      kind: 'semantic_view_workbench',
      name: queryName || '',
      description: queryDesc || '',
      sql_text: sourceSql,
      semantic_yaml: lastYaml?.result_yaml || '',
      conversion_settings: { view_name: viewName || '' },
      workbench_step0: serializeStep(step0),
      workbench_steps: steps.map(serializeStep),
      exported_at: new Date().toISOString(),
    }
    return JSON.stringify(payload, null, 2)
  }, [queryName, queryDesc, sourceSql, step0, steps, viewName])

  // 파일/GCS 에서 읽은 워크벤치 JSON 을 현재 작업에 적용(덮어쓰기 confirm).
  const applyWorkbenchImport = useCallback((text) => {
    let data
    try { data = JSON.parse(text) } catch { showToast('error', 'JSON 파싱 실패 — 올바른 워크벤치(.svwb.json) 파일이 아닙니다.'); return }
    if (!data || typeof data !== 'object') { showToast('error', '워크벤치 형식이 아닙니다.'); return }
    if (data.kind && data.kind !== 'semantic_view_workbench') {
      if (!window.confirm('워크벤치(.svwb.json) 형식이 아닐 수 있습니다. 그래도 가져올까요?')) return
    }
    if (!window.confirm('현재 작업 내용을 가져온 워크벤치로 덮어씁니다. 계속할까요? (저장 전 상태)')) return
    // KBNL-105: 새 세션 키 — 가져온 내용은 미저장 상태.
    newWbSeqRef.current += 1
    wbKeyRef.current = `new-${newWbSeqRef.current}`
    setSelectedQuery(null)
    setSelectedQueryIsMine(true)
    setSelectedQueryOwner('')
    setSelectedQueryTokenCount(null)
    setQueryName(data.name || '')
    setQueryDesc(data.description || '')
    setViewName(data.conversion_settings?.view_name || '')
    setSourceSql(data.sql_text || '')
    setStep0(data.workbench_step0 ? migrateStepShape(data.workbench_step0) : { ...makeStep({ kind: 'yaml_generate' }), save_as_view: true })
    setSteps(Array.isArray(data.workbench_steps) ? data.workbench_steps.map(migrateStepShape) : [])
    setActiveStepKey('step-0')
    setLoadedView(null)
    showToast('success', `워크벤치 '${data.name || '(이름 없음)'}' 가져오기 완료 — 저장하려면 저장 버튼을 누르세요.`)
  }, [showToast])

  // ── KBNL-111: 패널 📂 불러오기 — Firestore 항목을 검색 목록에서 골라 내용 주입 ──
  // isYaml=true → Semantic View + Composite View, false → SQL 쿼리 목록.
  const openLoad = useCallback((isYaml, onLoaded) => {
    setLoadPickerSearch('')
    setLoadPicker({ isYaml, onLoaded })
  }, [])

  // 선택 항목의 내용을 fetch 해서 패널에 그대로 주입.
  const doLoad = useCallback(async (item) => {
    if (!loadPicker) return
    setLoadPickerBusy(true)
    try {
      let content = ''
      if (item._type === 'sv') {
        content = (await api.getView(item.name, storageSettings, item.owner_user_id || '')).semantic_yaml || ''
      } else if (item._type === 'cv') {
        content = (await api.getCompositeView(item.name, storageSettings, item.owner_user_id || '')).composite_yaml || ''
      } else {
        content = (await api.getSqlQuery(item.name, storageSettings, item.owner_user_id || '')).sql_text || ''
      }
      loadPicker.onLoaded(content)
      showToast('success', `'${item.name}' 불러오기 완료`)
      setLoadPicker(null)
    } catch (e) {
      showToast('error', `불러오기 실패: ${e.message}`)
    } finally {
      setLoadPickerBusy(false)
    }
  }, [loadPicker, storageSettings, showToast])




  // ── 좌측 검색 필터 ───────────────────────────────────────────────────
  const filteredSavedQueries = useMemo(() => {
    const base = showOthersWb ? savedQueries : savedQueries.filter(q => q.is_mine !== false)
    return filterByText(base, sqlQuery, ['name', 'description'])
  }, [savedQueries, sqlQuery, showOthersWb])
  // KBNL-141(리뷰): 선택 항목 식별을 owner::name 복합 키로 — 동명이인 행 동시 선택 방지.
  const selectedQueryKey = selectedQuery ? `${selectedQueryOwner}::${selectedQuery}` : ''

  const cmTheme = editorTheme === 'light' ? undefined : editorTheme

  // ── 우측 step 네비게이션 — 각 step 카드 DOM 에 ref 를 걸어 스크롤 ───────
  const stepRefs = useRef({})
  const setStepRef = useCallback((key) => (el) => {
    if (el) stepRefs.current[key] = el
    else delete stepRefs.current[key]
  }, [])
  const scrollToStep = useCallback((key) => {
    const el = stepRefs.current[key]
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    <div className={`wb-root ${leftCollapsed ? 'wb-left-collapsed' : ''}`} ref={wbRootRef}>
      {/* KBNL-99 §8: 좌측 패널 접기/펴기 토글 — sidebar 가 숨겨졌을 때만 노출
          (펴기 버튼). 펼친 상태에서의 접기 버튼은 사이드바 헤더 안에 둠. */}
      {leftCollapsed && (
        <button
          className="wb-left-expand-btn"
          onClick={toggleLeftCollapsed}
          title="좌측 패널 펼치기"
          style={{
            position: 'absolute', top: 4, left: 4, zIndex: 10,
            fontSize: 13, padding: '2px 8px',
            background: 'var(--bg-sidebar)', border: '1px solid var(--border)',
            borderRadius: 4, cursor: 'pointer',
          }}
        >▶ 목록</button>
      )}
      {/* ── 사이드바 ────────────────────────────────────────────────── */}
      <div className="composite-sidebar wb-sidebar" style={{ display: leftCollapsed ? 'none' : undefined }}>
        <div className="composite-sidebar-header">
          <span style={{ fontWeight: 600, fontSize: 13 }}>🧰 워크벤치</span>
          {/* KBNL-141: 소유자 필터 토글 — 기본 '내것만' */}
          <button
            className="btn-xs cv-owner-toggle"
            onClick={() => setShowOthersWb(v => !v)}
            title={showOthersWb ? '클릭하면 내 워크벤치만 표시' : '클릭하면 다른 사람의 워크벤치도 함께 표시'}
            style={{ marginLeft: 'auto' }}
          >{showOthersWb ? '👥 전체' : '🙋 내것만'}</button>
          {/* KBNL-99 §8: 좌측 패널 접기 (결과 집중 모드) */}
          <button
            className="btn-xs"
            onClick={toggleLeftCollapsed}
            title="좌측 패널 접기 — 결과 화면 집중"
            style={{ fontSize: 12, lineHeight: 1, padding: '0 4px', marginLeft: 4 }}
          >◀</button>
        </div>
        {/* Composite View 사이드바의 '+ 새 ...' 와 동일 스타일(.btn-new) — 시각 일관성 */}
        <button className="btn-new" onClick={handleNew}>
          <span>+</span> 새 워크벤치
        </button>
        <div style={{ display: 'flex', padding: '4px 8px', borderBottom: '1px solid var(--border)', background: 'var(--bg-sidebar)' }}>
          <SearchInput
            value={sqlQuery}
            onChange={setSqlQuery}
            placeholder="SQL 검색 (Ctrl+K)"
            total={showOthersWb ? savedQueries.length : savedQueries.filter(q => q.is_mine !== false).length}
            matched={filteredSavedQueries.length}
            compact
          />
        </div>
        <div className="std-list">
          {savedQueries.length === 0 ? (
            <div className="std-list-empty">저장된 SQL이 없습니다.</div>
          ) : filteredSavedQueries.length === 0 ? (
            <div className="std-list-empty">조건에 맞는 SQL 없음</div>
          ) : (
            filteredSavedQueries.map(q => {
              const isMine = q.is_mine !== false
              const ownerName = q.owner_name || q.owner_user_id || ''
              const tooltip = isMine
                ? '내가 만든 SQL (더블클릭으로 이름 변경)'
                : `작성자: ${ownerName} — 읽기 전용 (저장 시 내 워크스페이스에 복사)`
              // KBNL-114: Step 0 만 있어도 워크벤치 — workbench_step0 도 함께 인식.
              const hasWb = (Array.isArray(q.workbench_steps) && q.workbench_steps.length > 0)
                || (q.workbench_step0 != null && typeof q.workbench_step0 === 'object')
              // KBNL-141(리뷰): owner::name 복합 키로 선택/리네임 판정 (동명이인 충돌 방지).
              const qKey = `${q.owner_user_id || ''}::${q.name}`
              const isRenamingThis = renamingQuery === qKey
              return (
                <div
                  key={qKey}
                  onClick={() => { if (!isRenamingThis) handleSelectQuery(q.name, q.owner_user_id || '') }}
                  onDoubleClick={() => { if (isMine) handleStartRename(q) }}
                  className={`std-list-item ${selectedQueryKey === qKey ? 'selected' : ''}`}
                  title={tooltip}
                >
                  <div className="std-list-name-row">
                    {isRenamingThis ? (
                      <input
                        autoFocus
                        className="std-list-rename-input"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleConfirmRename()
                          if (e.key === 'Escape') handleCancelRename()
                        }}
                        onBlur={handleConfirmRename}
                        onClick={e => e.stopPropagation()}
                        onDoubleClick={e => e.stopPropagation()}
                      />
                    ) : (
                      <span className="std-list-name">
                        <HighlightText text={q.name} query={sqlQuery} />
                        {hasWb && <span className="wb-badge" title="이 SQL 에는 Workbench step 들이 저장되어 있습니다">WB</span>}
                      </span>
                    )}
                    {/* KBNL-141: 워크벤치 변환 진행 중이면 목록에 스피너 */}
                    {!isRenamingThis && runningJobs.workbench.has(qKey) && (
                      <span className="spinner" title="변환 진행 중…"
                        style={{ width: 12, height: 12, flexShrink: 0 }} />
                    )}
                    {!isRenamingThis && (
                      <span className="std-list-owner" title={tooltip}>
                        {isMine
                          ? <span className="std-list-user-badge">나</span>
                          : <HighlightText text={ownerName} query={sqlQuery} />
                        }
                      </span>
                    )}
                    {/* 본인 항목에만 노출되는 hover 액션 — 복제 (workbench_steps + 연결 SV/CV 함께) */}
                    {isMine && !isRenamingThis && (
                      <button
                        className="std-list-action-btn"
                        title="복제 (Workbench step + 연결된 Semantic / Composite View 까지 함께 복제)"
                        onClick={(e) => { e.stopPropagation(); openDuplicate(q) }}
                      >⎘</button>
                    )}
                  </div>
                  {!isRenamingThis && q.description && (
                    <div className="std-list-desc">
                      <HighlightText text={q.description} query={sqlQuery} />
                    </div>
                  )}
                  {!isRenamingThis && (q.updated_at || q.token_count != null) && (
                    <div className="std-list-meta">
                      {q.updated_at && <span>{String(q.updated_at).slice(0, 10)}</span>}
                      <TokenBadge count={q.token_count} />
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* ── 본체: 헤더 + step list ─────────────────────────────────── */}
      <div className="wb-main">
        {/* 워크벤치 헤더 — SqlConverter 와 동일한 3행 레이아웃
            행1: [워크벤치 이름] [저장][삭제][+새 워크벤치]
            행2: [설명 — field-desc textarea]
            행3: [View 이름] [type 배지] */}
        <div className="composite-save-bar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap', minWidth: 72 }}>
              워크벤치 이름
            </label>
            <input
              value={queryName}
              onChange={e => setQueryName(e.target.value)}
              placeholder="워크벤치(=SQL Query) 이름"
              className="field field-name"
            />
            {/* KBNL-69: 본문 헤더 토큰 배지 — sql_text 기준 (저장 시 갱신) */}
            {selectedQuery && (
              <TokenBadge
                count={selectedQueryTokenCount}
                variant="strong"
                title="이 워크벤치 SQL 의 저장된 토큰 수 — 저장 시 갱신"
              />
            )}
            <div className="btn-actions">
              {/* KBNL-110 정리: 상단 'GCS Export'(최종 YAML) 버튼 제거 — 각 step 의
                  변환 후 패널 'GCS Export' / '💾 저장' 으로 대체됨(중복). */}
              <button className="btn-secondary" onClick={handleSave} disabled={saving || !queryName.trim() || !sourceSql.trim()} title="워크벤치 프로젝트(소스+step들)를 Firestore 에 저장">
                {saving ? '저장 중...' : '워크벤치 저장'}
              </button>
              {selectedQuery ? (
                <>
                  <button className="btn-danger" onClick={handleDelete} title="삭제">삭제</button>
                  <button className="btn-secondary" onClick={handleNew} title="새 워크벤치">+ 새 워크벤치</button>
                </>
              ) : (
                <button className="btn-secondary" onClick={handleNew} title="비우기">초기화</button>
              )}
              {/* KBNL-109: 워크벤치 통째(소스+step들+설정) 파일/GCS 전송.
                  KBNL-141: 본문(step) 버튼들과 동일한 화살표 이모지 + 한글 레이블로 통일.
                  대상이 '워크벤치 전체'임은 앞의 그룹 레이블로 구분. */}
              <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)', margin: '0 2px' }} />
              <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap', alignSelf: 'center' }}>워크벤치 전체:</span>
              <button
                className="btn-xs"
                title="워크벤치 전체를 파일(.svwb.json)로 다운로드"
                onClick={() => downloadText(ensureExtension(`${queryName || 'workbench'}.svwb`, 'json'), buildWorkbenchExportJson(), 'application/json')}
                style={{ fontSize: 11 }}
              >⤓ 다운로드</button>
              <button
                className="btn-xs"
                title="파일(.svwb.json)에서 워크벤치 전체 가져오기"
                onClick={() => pickLocalFile(applyWorkbenchImport)}
                style={{ fontSize: 11 }}
              >⤴ 파일</button>
              <button
                className="btn-xs"
                title="워크벤치 전체를 GCS 로 Export"
                onClick={() => setExportSource({
                  content: buildWorkbenchExportJson(),
                  defaultFilename: ensureExtension(`${queryName || 'workbench'}.svwb`, 'json'),
                  contentType: 'application/json',
                  title: '워크벤치 전체를 GCS 로 Export',
                })}
                style={{ fontSize: 11 }}
              >⤴ GCS Export</button>
              <button
                className="btn-xs"
                title="GCS 에서 워크벤치 전체 가져오기"
                onClick={() => setImportTarget({ onImported: applyWorkbenchImport })}
                style={{ fontSize: 11 }}
              >⤵ GCS 가져오기</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: descExpanded ? 'flex-start' : 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-dim)', whiteSpace: 'nowrap', minWidth: 72, paddingTop: descExpanded ? 4 : 0 }}>
              설명
            </label>
            <textarea
              value={queryDesc}
              onChange={e => setQueryDesc(e.target.value)}
              placeholder="이 워크벤치에 대한 설명 (선택)"
              className={`field field-desc ${descExpanded ? 'expanded' : 'collapsed'}`}
              rows={descExpanded ? 3 : 1}
              style={descExpanded ? {} : { resize: 'none', overflow: 'hidden', whiteSpace: 'nowrap', minHeight: 'auto' }}
            />
            <button
              type="button"
              className="btn-xs"
              onClick={() => setDescExpanded(v => !v)}
              title={descExpanded ? '설명 영역 접기' : '설명 영역 펼치기'}
              style={{ fontSize: 11, padding: '0 6px', flexShrink: 0 }}
            >{descExpanded ? '▴' : '▾'}</button>
          </div>
          {/* KBNL-99: 헤더의 'View 이름' 행 제거 — 각 yaml step 의 우측 패널의
              '별도 저장' 체크 + 이름 입력으로 대체. viewName state 는 옛 doc
              로드 호환을 위해 hidden 으로만 유지. */}
          <input type="hidden" value={viewName} onChange={e => setViewName(e.target.value)} />
        </div>

        {/* KBNL-106: 다른 창에서 같은 워크벤치 변환 중 — 스피너 배너 + ▶ 비활성.
            눈에 잘 띄게 앰버(주의) 계열 + 굵은 글씨 (사용자 피드백). */}
        {remoteRun && (
          <div
            className="wb-remote-run-banner"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 12px', margin: '4px 0', fontSize: 13, fontWeight: 600,
              color: '#7c2d12',
              background: 'linear-gradient(90deg, rgba(245,158,11,0.28), rgba(245,158,11,0.14))',
              border: '1.5px solid #f59e0b', borderLeft: '6px solid #f59e0b',
              borderRadius: 6, boxShadow: '0 1px 4px rgba(245,158,11,0.35)',
            }}
          >
            <div className="spinner" style={{ width: 16, height: 16, flexShrink: 0 }} />
            <span>
              ⚠️ 다른 창에서 이 워크벤치를 변환 중입니다
              {remoteRun.user_name ? ` — ${remoteRun.user_name}` : ''}
              {remoteRun.step_label ? ` (${remoteRun.step_label})` : ''}
              . 완료 후 저장되면 목록에서 다시 열어 확인하세요.
            </span>
          </div>
        )}

        {/* KBNL-126: 서버측 잡 배너 — 진행 중(복원 스피너)/완료 미회수/중단/에러.
            로컬에서 이미 스피너가 도는 step 의 running 잡은 숨김(중복 방지). */}
        {wbJobs.filter(j => {
          if (j.status !== 'running') return true
          const sid = String(j?.step?.id ?? '')
          if (String(step0.id) === sid) return step0.status !== 'running'
          const s = steps.find(x => String(x.id) === sid)
          return !s || s.status !== 'running'
        }).map(j => {
          const stepDesc = `Step ${j?.step?.no ?? '?'} — ${labelForKind(j?.step?.kind) || j?.step?.kind || ''}${j?.step?.engine === 'llm' ? ' (LLM)' : ''}`
          const base = {
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px', margin: '4px 0', fontSize: 13, fontWeight: 600,
            borderRadius: 6,
          }
          if (j.status === 'running') {
            return (
              <div key={j.job_id} style={{ ...base, color: '#1d4ed8', background: 'rgba(37,99,235,0.10)', border: '1.5px solid #2563eb', borderLeft: '6px solid #2563eb' }}>
                <div className="spinner" style={{ width: 16, height: 16, flexShrink: 0 }} />
                <span style={{ flex: 1 }}>서버에서 변환 진행 중 — {stepDesc}. 완료되면 여기서 가져올 수 있습니다.</span>
                {/* KBNL-141: 진행 중 강제 중단 */}
                <button className="btn-xs" onClick={() => cancelJob(j)} style={{ fontSize: 11 }}
                  title="진행 중인 변환 중단 (결과 폐기)">■ 중단</button>
              </div>
            )
          }
          if (j.status === 'done') {
            return (
              <div key={j.job_id} style={{ ...base, color: '#166534', background: 'rgba(22,163,74,0.10)', border: '1.5px solid #16a34a', borderLeft: '6px solid #16a34a' }}>
                <span style={{ flex: 1 }}>✅ 이전 변환 결과가 있습니다 — {stepDesc}</span>
                <button className="btn-primary btn-xs" onClick={() => collectJob(j)} style={{ fontSize: 11 }}>가져오기</button>
                <button className="btn-xs" onClick={() => removeJobLocal(j.job_id)} style={{ fontSize: 11 }}>버리기</button>
              </div>
            )
          }
          // interrupted / error
          const isErr = j.status === 'error'
          return (
            <div key={j.job_id} style={{ ...base, color: '#7c2d12', background: 'rgba(245,158,11,0.18)', border: '1.5px solid #f59e0b', borderLeft: '6px solid #f59e0b' }}>
              <span style={{ flex: 1 }}>
                {isErr
                  ? `❌ 변환 실패 — ${stepDesc}${j.error ? ` · ${String(j.error).slice(0, 120)}` : ''}`
                  : `⚠️ 변환이 중단된 것으로 보입니다 (서버 응답 없음) — ${stepDesc}`}
              </span>
              <button className="btn-primary btn-xs" onClick={() => retryJob(j)} style={{ fontSize: 11 }}>다시 실행</button>
              <button className="btn-xs" onClick={() => removeJobLocal(j.job_id)} style={{ fontSize: 11 }}>정리</button>
            </div>
          )
        })}

        {/* Step list */}
        <div className="wb-steps">
          {/* ── Step 0: 소스 SQL 또는 YAML — KBNL-99 §4 + 2컬럼 ─────────────
              KBNL-99 후속: 활성 step 일 때만 렌더 (스탭간 이동 = STEPS nav). */}
          {activeStepKey === 'step-0' && (
          <div className={`wb-step wb-step-source wb-step-kind-${step0.kind} ${isYamlKind(step0.kind) ? 'wb-step-final' : ''} wb-step-status-${step0.status}`} ref={setStepRef('step-0')}>
            <div className="wb-step-head">
              <span className="wb-step-num">Step 0</span>
              {/* KBNL-103: Step 0 도 변환 종류 선택 가능 (소스→결과). 상위 step 이
                  없으므로 base 는 항상 소스(독립 고정) — 누적/독립 토글은 숨김. */}
              <select
                className="wb-step-kind-select"
                value={step0.kind}
                onChange={e => setStepKind(STEP0_IDX, e.target.value)}
                disabled={step0.status === 'running'}
                title="이 step 의 변환 종류 — 바꾸면 결과 초기화"
                style={{ fontSize: 12 }}
              >
                <option value="sql_refine">SQL 정제 (SQL→SQL)</option>
                <option value="yaml_generate">YAML 생성 (SQL→YAML)</option>
                <option value="yaml_refine">YAML 정제 (YAML→YAML)</option>
                <option value="yaml_to_sql">YAML → SQL</option>
              </select>
              <TypeFlowBadge kind={step0.kind} />
              {/* KBNL-120: yaml_to_sql 변환 엔진 선택 (규칙=결정적 | LLM=유연·지시 반영) */}
              {step0.kind === 'yaml_to_sql' && (
                <select
                  className="wb-step-engine-select"
                  value={step0.engine === 'llm' ? 'llm' : 'rule'}
                  onChange={e => setStepEngine(STEP0_IDX, e.target.value)}
                  disabled={step0.status === 'running'}
                  title="YAML→SQL 변환 엔진 — 규칙: 결정적·재현성 보장 / LLM: 유연·가독성·프롬프트 반영"
                  style={{ fontSize: 11 }}
                >
                  <option value="rule">엔진: 규칙</option>
                  <option value="llm">엔진: LLM</option>
                </select>
              )}
              <button
                className="btn-primary btn-xs wb-step-run"
                onClick={() => runStep(STEP0_IDX)}
                disabled={step0.status === 'running' || !sourceSql.trim() || !!remoteRun}
                title={remoteRun ? '다른 창에서 이 워크벤치를 변환 중입니다' : `${labelForKind(step0.kind)} 실행${!step0.prompt.trim() ? ' (기본 프롬프트로)' : ''}`}
              >
                {step0.status === 'running' ? '실행 중...' : runLabelForKind(step0.kind)}
              </button>
              {/* KBNL-99: 하이라이트 override 토글 — 자동(소스 입력 타입) / SQL / YAML */}
              <span className="wb-hl-toggle" style={{ display: 'inline-flex', gap: 2, marginLeft: 8 }} title="소스 하이라이트 모드">
                {[
                  { v: null, label: '자동' },
                  { v: 'sql', label: 'SQL' },
                  { v: 'yaml', label: 'YAML' },
                ].map(opt => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setHighlightOverride(opt.v)}
                    className="btn-xs"
                    style={{
                      fontSize: 11, padding: '0 6px',
                      background: highlightOverride === opt.v ? 'var(--accent, #2563eb)' : 'transparent',
                      color: highlightOverride === opt.v ? '#fff' : 'var(--text)',
                      border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer',
                    }}
                  >{opt.label}</button>
                ))}
              </span>
              {highlightOverride === null && (
                <span className="wb-step-meta" style={{ marginLeft: 6 }}>
                  → {sourceHighlight.toUpperCase()} (첫 step 기준)
                </span>
              )}
              <span className="wb-step-meta" style={{ marginLeft: 8 }}>
                {sourceSql.split('\n').length} lines
              </span>
              <span style={{ flex: 1 }} />
              {/* KBNL-136(feedback 20260618): 변환 전/후 스크롤 동기화 토글 — step 헤더 우측 */}
              <button
                className="btn-xs"
                onClick={() => setScrollSync(s => !s)}
                aria-pressed={scrollSync}
                title="변환 전/후 두 편집기의 줄 위치를 맞춰 함께 스크롤 (짧은 쪽은 하단 여백)"
                style={{
                  fontSize: 11,
                  border: '1px solid ' + (scrollSync ? '#3b82f6' : 'var(--border)'),
                  background: scrollSync ? '#3b82f6' : 'transparent',
                  color: scrollSync ? '#fff' : 'var(--text)',
                }}
              >{scrollSync ? '🔗 스크롤 동기화 ON' : '🔗 스크롤 동기화'}</button>
              {/* KBNL-107 후속: 소스 IO(복사/파일/다운로드/GCS/저장) 버튼은 아래
                  '변환 전' 패널 머리로 이동 — 일반 step 과 위치 일관화. */}
            </div>
            {/* KBNL-103: Step 0 변환 프롬프트 — 다른 step 과 동일 (소스→결과). */}
            {(() => {
              const expanded = promptExpandedSet.has(step0.id)
              const ph = step0.kind === 'sql_refine'
                ? '정제 프롬프트 (예: alias 를 정리하고 GROUP BY 키만 dimension 으로 두세요)'
                : step0.kind === 'yaml_generate'
                  ? '변환 프롬프트 (예: 단일 매출 메트릭 뷰로 만들어주세요)'
                  : '정제 프롬프트 (예: dimension_group 의 시간 단위를 일/월/년 모두로 늘려주세요)'
              return (
                <div className="wb-prompt-row" style={{ display: 'flex', gap: 6, alignItems: expanded ? 'flex-start' : 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap', paddingTop: expanded ? 6 : 0 }}>프롬프트</span>
                  <textarea
                    value={step0.prompt}
                    onChange={e => setStepPrompt(STEP0_IDX, e.target.value)}
                    placeholder={ph}
                    className={`field field-prompt wb-prompt ${expanded ? 'expanded' : 'collapsed'}`}
                    rows={expanded ? 3 : 1}
                    style={expanded ? { flex: 1 } : { flex: 1, resize: 'none', overflow: 'hidden', whiteSpace: 'nowrap', minHeight: 'auto' }}
                    disabled={step0.status === 'running'}
                  />
                  <button
                    type="button"
                    className="btn-xs"
                    onClick={() => togglePromptExpanded(step0.id)}
                    title={expanded ? '프롬프트 접기' : '프롬프트 펼치기'}
                    style={{ fontSize: 11, padding: '0 6px', flexShrink: 0 }}
                  >{expanded ? '▴' : '▾'}</button>
                </div>
              )
            })()}
            {/* KBNL-103: 좌(변환 전 = 소스) / 우(변환 후 = 결과) — 다른 step 과 동일 */}
            <div className="wb-step-split">
              <div className="wb-step-pane wb-step-pane-base">
                <div className="wb-step-head wb-pane-head">
                  <span className="wb-result-label">변환 전 ({sourceHighlight.toUpperCase()})</span>
                  <span className="wb-step-meta">소스 (변환의 출발점)</span>
                  <span className="wb-pane-head-break" />
                  {/* KBNL-107 후속: 소스(변환 전) — 복사 / 파일·다운로드 / GCS Im·Ex / 저장 */}
                  <CopyButton value={sourceSql} disabled={!sourceSql.trim()} style={{ fontSize: 11 }} />
                  <button
                    className="btn-xs"
                    onClick={() => setImportTarget({ onImported: (content) => setSourceSql(content) })}
                    style={{ fontSize: 11 }}
                    title="GCS 에서 소스 불러오기"
                  >⤵ GCS</button>
                  {SHOW_LOCAL_FILE_IO && (<>
                  <button
                    className="btn-xs"
                    onClick={() => pickLocalFile((text) => setSourceSql(text))}
                    style={{ fontSize: 11 }}
                    title="로컬 파일에서 소스 불러오기"
                  >⤴ 파일</button>
                  <button
                    className="btn-xs"
                    onClick={() => {
                      const ext = sourceHighlight === 'yaml' ? 'yaml' : 'sql'
                      const mime = sourceHighlight === 'yaml' ? 'application/yaml' : 'application/sql'
                      downloadText(ensureExtension(queryName || 'source', ext), sourceSql, mime)
                    }}
                    disabled={!sourceSql.trim()}
                    style={{ fontSize: 11 }}
                    title="소스 다운로드"
                  >⤓ 다운로드</button>
                  </>)}
                  <button
                    className="btn-xs"
                    onClick={() => {
                      const ext = sourceHighlight === 'yaml' ? 'yaml' : 'sql'
                      const mime = sourceHighlight === 'yaml' ? 'application/yaml' : 'application/sql'
                      setExportSource({
                        content: sourceSql,
                        defaultFilename: ensureExtension(queryName || 'source', ext),
                        contentType: mime,
                        title: '소스(변환 전)를 GCS 로 Export',
                      })
                    }}
                    disabled={!sourceSql.trim()}
                    style={{ fontSize: 11 }}
                    title="소스를 GCS 로 Export"
                  >⤴ GCS Export</button>
                  <button
                    className="btn-xs"
                    onClick={() => openTypeSave(sourceSql, sourceHighlight === 'yaml')}
                    disabled={!sourceSql.trim()}
                    style={{ fontSize: 11 }}
                    title="소스를 type 에 맞게 Firestore 에 저장 (SQL/SV/CV)"
                  >💾 저장</button>
                  <button
                    className="btn-xs"
                    onClick={() => openLoad(sourceHighlight === 'yaml', (c) => setSourceSql(c))}
                    style={{ fontSize: 11 }}
                    title="Firestore 에서 불러와 소스에 채우기 (덮어쓰기)"
                  >📂 불러오기</button>
                </div>
                <div className="wb-editor-wrap editor-wrap">
                  {sourceHighlight === 'yaml' ? (
                    <CodeMirror
                      value={sourceSql}
                      onChange={setSourceSql}
                      extensions={[yaml(), search({ top: true }), Prec.highest(keymap.of([...searchKeymap, { key: 'Mod-Shift-l', run: selectSelectionMatches, preventDefault: true }]))]}
                      theme={cmTheme}
                      style={{ height: '100%' }}
                      onAlignPeer={alignPeerToView}
                      basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
                      placeholder="변환할 소스를 입력하세요 (SQL 또는 Semantic/Composite View YAML)."
                    />
                  ) : (
                    <CodeMirror
                      value={sourceSql}
                      onChange={setSourceSql}
                      extensions={sqlExtensions}
                      theme={cmTheme}
                      style={{ height: '100%' }}
                      onAlignPeer={alignPeerToView}
                      basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
                      placeholder="변환할 소스를 입력하세요 (SQL 또는 Semantic/Composite View YAML)."
                    />
                  )}
                </div>
              </div>
              {/* ── 우측: 변환 후 (result) — KBNL-103: 다른 step 과 동일 ─── */}
              {(() => {
                const isYaml = isYamlKind(step0.kind)
                const resultText = isYaml ? step0.result_yaml : step0.result_sql
                return (
              <div className="wb-step-pane wb-step-pane-result">
                <div className="wb-step-head wb-pane-head wb-result-head">
                  <span className="wb-result-label">변환 후 ({isYaml ? 'YAML' : 'SQL'})</span>
                  <span className="wb-step-meta">{resultText.split('\n').length} lines</span>
                  {isYaml && step0.result_yaml && (
                    <span className={`sql-type-badge ${typeBadgeClass(detectYamlType(step0.result_yaml))}`}>
                      {typeLabel(detectYamlType(step0.result_yaml))}
                    </span>
                  )}
                  {/* KBNL-110 정리: '별도 저장' 토글+이름란 제거 — '💾 저장' 으로 대체 */}
                  <span className="wb-pane-head-break" />
                  {/* KBNL-101: 존재하지 않는 테이블/컬럼 검증 */}
                  {(() => {
                    const vs = validateState[step0.id]
                    const running = vs?.running
                    return (
                      <button
                        className="btn-xs"
                        onClick={() => validateStep(step0)}
                        disabled={!resultText || running}
                        style={{ fontSize: 11 }}
                        title={isYaml ? 'Semantic View 의 테이블/컬럼 존재 검증' : 'SQL 의 테이블/컬럼 존재 검증 (dry-run)'}
                      >{running ? '검증 중…' : '🔎 스키마 검증'}</button>
                    )
                  })()}
                  {isYaml && step0.result_yaml && (() => {
                    const t = detectYamlType(step0.result_yaml)
                    if (t !== 'single' && t !== 'composite') return null
                    return renderRecommendPicker(step0.result_yaml, t, 'step-0', {}, 'xs')
                  })()}
                  <CopyButton value={resultText} disabled={!resultText} style={{ fontSize: 11 }} />
                  {/* KBNL-107: 변환 후 — GCS Import 추가 (4세트 완성) */}
                  <button
                    className="btn-xs"
                    onClick={() => setImportTarget({ onImported: (content) => {
                      if (isYaml) setStepResultYaml(STEP0_IDX, content)
                      else setStepResultSql(STEP0_IDX, content)
                    } })}
                    style={{ fontSize: 11 }}
                    title="GCS 에서 결과 불러오기 (덮어쓰기)"
                  >⤵ GCS</button>
                  {SHOW_LOCAL_FILE_IO && (<>
                  <button
                    className="btn-xs"
                    onClick={() => pickLocalFile((text) => {
                      if (isYaml) setStepResultYaml(STEP0_IDX, text)
                      else setStepResultSql(STEP0_IDX, text)
                    })}
                    style={{ fontSize: 11 }}
                    title="로컬 파일에서 결과 불러오기 (덮어쓰기)"
                  >⤴ 파일</button>
                  <button
                    className="btn-xs"
                    onClick={() => {
                      const ext  = isYaml ? 'yaml' : 'sql'
                      const mime = isYaml ? 'text/yaml' : 'application/sql'
                      const fname = ensureExtension(`${queryName || 'workbench'}_step0`, ext)
                      downloadText(fname, resultText, mime)
                    }}
                    disabled={!resultText}
                    style={{ fontSize: 11 }}
                    title="결과 다운로드"
                  >⤓ 다운로드</button>
                  </>)}
                  <button
                    className="btn-xs"
                    onClick={() => {
                      const ext  = isYaml ? 'yaml' : 'sql'
                      const mime = isYaml ? 'text/yaml' : 'application/sql'
                      const stepVName = (step0.view_name || '').trim() || `${queryName || 'workbench'}_step0`
                      setExportSource({
                        content: resultText,
                        defaultFilename: ensureExtension(stepVName, ext),
                        contentType: mime,
                        title: `Step 0 (${labelForKind(step0.kind)}) 결과를 GCS 로 Export`,
                      })
                    }}
                    disabled={!resultText}
                    style={{ fontSize: 11 }}
                    title="이 step 결과를 GCS 로 Export"
                  >⤴ GCS Export</button>
                  <button
                    className="btn-xs"
                    onClick={() => openTypeSave(resultText, isYaml)}
                    disabled={!resultText}
                    style={{ fontSize: 11 }}
                    title="이 결과를 type 에 맞게 Firestore 에 저장 (SQL/SV/CV)"
                  >💾 저장</button>
                  <button
                    className="btn-xs"
                    onClick={() => openLoad(isYaml, (c) => { if (isYaml) setStepResultYaml(STEP0_IDX, c); else setStepResultSql(STEP0_IDX, c) })}
                    style={{ fontSize: 11 }}
                    title="Firestore 에서 불러와 결과에 채우기 (덮어쓰기)"
                  >📂 불러오기</button>
                </div>
                <div className="wb-editor-wrap editor-wrap">
                  {isYaml ? (
                    <CodeMirror
                      value={step0.result_yaml}
                      onChange={(v) => setStepResultYaml(STEP0_IDX, v)}
                      extensions={yamlExtensions}
                      theme={cmTheme}
                      style={{ height: '100%' }}
                      onAlignPeer={alignPeerToView}
                      basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
                      placeholder="실행 시 LLM 의 변환 결과 YAML 이 채워집니다. 직접 편집 가능."
                    />
                  ) : (
                    <CodeMirror
                      value={step0.result_sql}
                      onChange={(v) => setStepResultSql(STEP0_IDX, v)}
                      extensions={sqlExtensions}
                      theme={cmTheme}
                      style={{ height: '100%' }}
                      onAlignPeer={alignPeerToView}
                      basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
                      placeholder="실행 시 LLM 의 정제 결과 SQL 이 채워집니다. 직접 편집 가능."
                    />
                  )}
                  {step0.status === 'running' && (
                    <div className="wb-step-overlay">
                      <div className="spinner" />
                      <div className="wb-step-overlay-text">
                        Step 0 {labelForKind(step0.kind)} 중...
                      </div>
                    </div>
                  )}
                </div>
                {/* KBNL-101: 검증 결과 패널 */}
                {(() => {
                  const vr = validateState[step0.id]?.result
                  // KBNL-136(feedback 20260618): 성공(✓)은 토스트로 충분 — 중복 패널 제거.
                  // 패널은 실패 시에만 (오류 목록을 보여줘야 하므로). 토스트는 양쪽 다 유지.
                  if (!vr || vr.valid) return null
                  const probs = [
                    ...(vr.unknown_tables || []).map(t => `테이블 없음: ${t}`),
                    ...(vr.unknown_columns || []).map(c => `컬럼 없음: ${c}`),
                  ]
                  const errs = probs.length > 0 ? probs : (vr.errors || [])
                  return (
                    <div className="wb-validate-result" style={{
                      position: 'relative',
                      padding: '4px 24px 4px 8px', fontSize: 11,
                      borderTop: '1px solid var(--border)',
                      background: 'rgba(192,57,43,0.08)',
                      color: '#c0392b',
                      maxHeight: 96, overflowY: 'auto',
                    }}>
                      <button
                        className="wb-validate-close"
                        onClick={() => dismissValidation(step0.id)}
                        title="검증 결과 닫기"
                        aria-label="검증 결과 닫기"
                        style={{
                          position: 'absolute', top: 2, right: 4, lineHeight: 1,
                          border: 'none', background: 'transparent', cursor: 'pointer',
                          color: 'inherit', fontSize: 13, opacity: 0.7, padding: '0 2px',
                        }}
                      >×</button>
                      <div>
                        <div style={{ fontWeight: 600 }}>✗ 검증 실패 — {errs.length}건 <span style={{ opacity: 0.6 }}>({vr.method})</span></div>
                        <ul style={{ margin: '2px 0 0', paddingLeft: 16 }}>
                          {errs.slice(0, 20).map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                      </div>
                    </div>
                  )
                })()}
              </div>
                )
              })()}
            </div>
          </div>
          )}

          {/* ── Step 1..N — KBNL-99: 활성 step 만 렌더. STEPS nav 에서 전환. */}
          {steps.map((s, idx) => {
            if (activeStepKey !== `step-${s.id}`) return null
            // (이하 기존 로직 그대로)
            const stepNo = idx + 1
            const isYaml = isYamlKind(s.kind)        // 결과가 YAML 인지
            const isYamlInput = (s.kind === 'yaml_refine' || s.kind === 'yaml_to_sql')  // 입력이 YAML 인지

            // base 힌트 — kind/mode 별
            let baseHint
            if (isYamlInput) {
              baseHint = s.mode === 'independent'
                ? '첫 yaml_generate 결과'
                : '직전 YAML 결과'
            } else {
              baseHint = s.mode === 'independent'
                ? '소스 SQL (Step 0)'
                : (idx === 0 ? '소스 SQL (Step 0)' : `직전 SQL 결과 (Step ${idx})`)
            }

            const resultText = isYaml ? s.result_yaml : s.result_sql
            const placeholderPrompt =
              s.kind === 'sql_refine'
                ? '정제 프롬프트 (예: alias 를 정리하고 GROUP BY 키만 dimension 으로 두세요)'
                : s.kind === 'yaml_generate'
                  ? '변환 프롬프트 (예: 단일 매출 메트릭 뷰로 만들어주세요)'
                  : '정제 프롬프트 (예: dimension_group 의 시간 단위를 일/월/년 모두로 늘려주세요)'

            return (
              <div key={s.id} ref={setStepRef(s.id)} className={`wb-step wb-step-kind-${s.kind} ${isYaml ? 'wb-step-final' : ''} wb-step-status-${s.status}`}>
                <div className="wb-step-head">
                  <span className="wb-step-num">Step {stepNo}</span>
                  {/* KBNL-99: 생성 후에도 변환 종류 변경 가능. 소스가 단일 텍스트라
                      모든 kind 항상 선택 가능 (yaml 입력 step 은 소스를 YAML 로 해석). */}
                  <select
                    className="wb-step-kind-select"
                    value={s.kind}
                    onChange={e => setStepKind(idx, e.target.value)}
                    disabled={s.status === 'running'}
                    title="이 step 의 변환 종류 — 바꾸면 결과 초기화"
                    style={{ fontSize: 12 }}
                  >
                    <option value="sql_refine">SQL 정제 (SQL→SQL)</option>
                    <option value="yaml_generate">YAML 생성 (SQL→YAML)</option>
                    <option value="yaml_refine">YAML 정제 (YAML→YAML)</option>
                    <option value="yaml_to_sql">YAML → SQL</option>
                  </select>
                  {/* KBNL-99: 입력→출력 type 색 뱃지 */}
                  <TypeFlowBadge kind={s.kind} />

                  {/* ▶ 실행 — 마우스 이동 거리를 줄이기 위해 좌측 (Step 제목 옆). */}
                  <button
                    className="btn-primary btn-xs wb-step-run"
                    onClick={() => runStep(idx)}
                    disabled={s.status === 'running' || !sourceSql.trim() || !!remoteRun}
                    title={`${labelForKind(s.kind)} 실행${!s.prompt.trim() ? ' (기본 프롬프트로)' : ''}`}
                  >
                    {s.status === 'running' ? '실행 중...' : runLabelForKind(s.kind)}
                  </button>

                  {/* mode 선택 (cumulative / independent) — kind 와 무관하게 모두 노출 */}
                  <span className="wb-mode-group" title={`Base ${isYamlInput ? 'YAML' : 'SQL'}: ${baseHint}`}>
                    <label className="wb-mode-radio">
                      <input
                        type="radio"
                        name={`mode-${s.id}`}
                        checked={s.mode === 'cumulative'}
                        onChange={() => setStepMode(idx, 'cumulative')}
                        disabled={s.status === 'running'}
                      /> 누적
                    </label>
                    <label className="wb-mode-radio">
                      <input
                        type="radio"
                        name={`mode-${s.id}`}
                        checked={s.mode === 'independent'}
                        onChange={() => setStepMode(idx, 'independent')}
                        disabled={s.status === 'running'}
                      /> 독립
                    </label>
                  </span>

                  {/* KBNL-120: yaml_to_sql 변환 엔진 선택 (규칙=결정적 | LLM=유연·지시 반영) */}
                  {s.kind === 'yaml_to_sql' && (
                    <select
                      className="wb-step-engine-select"
                      value={s.engine === 'llm' ? 'llm' : 'rule'}
                      onChange={e => setStepEngine(idx, e.target.value)}
                      disabled={s.status === 'running'}
                      title="YAML→SQL 변환 엔진 — 규칙: 결정적·재현성 보장 / LLM: 유연·가독성·프롬프트 반영"
                      style={{ fontSize: 11 }}
                    >
                      <option value="rule">엔진: 규칙</option>
                      <option value="llm">엔진: LLM</option>
                    </select>
                  )}

                  {/* yaml_* step — 결과 YAML 의 형식 자동 감지 배지 */}
                  {isYaml && s.result_yaml && (() => {
                    const t = detectYamlType(s.result_yaml)
                    return (
                      <span className={`sql-type-badge ${typeBadgeClass(t)}`} title="LLM 출력 YAML 의 자동 감지 형식">
                        {typeLabel(t)}
                      </span>
                    )
                  })()}

                  {s.edited && <span className="wb-edited-badge" title="결과를 직접 편집한 흔적이 있습니다">edited</span>}
                  {s.status === 'error' && <span className="wb-err-badge" title={s.error || ''}>error</span>}

                  {/* 우측엔 step 삭제만 — 위험 액션이라 실행 버튼과 분리해서 멀리 둠 */}
                  <span style={{ flex: 1 }} />
                  {/* KBNL-136(feedback 20260618): 변환 전/후 스크롤 동기화 토글 */}
                  <button
                    className="btn-xs"
                    onClick={() => setScrollSync(v => !v)}
                    aria-pressed={scrollSync}
                    title="변환 전/후 두 편집기의 줄 위치를 맞춰 함께 스크롤 (짧은 쪽은 하단 여백)"
                    style={{
                      fontSize: 11,
                      border: '1px solid ' + (scrollSync ? '#3b82f6' : 'var(--border)'),
                      background: scrollSync ? '#3b82f6' : 'transparent',
                      color: scrollSync ? '#fff' : 'var(--text)',
                    }}
                  >{scrollSync ? '🔗 동기화 ON' : '🔗 동기화'}</button>
                  <button
                    className="btn-danger btn-xs"
                    onClick={() => removeStep(idx)}
                    disabled={s.status === 'running'}
                    style={{ fontSize: 11 }}
                    title="이 step 삭제"
                  >×</button>
                </div>

                {/* 변환 프롬프트 — 기본 1줄, 토글 시 펼침 (KBNL-99 후속) */}
                {(() => {
                  const expanded = promptExpandedSet.has(s.id)
                  return (
                    <div className="wb-prompt-row" style={{ display: 'flex', gap: 6, alignItems: expanded ? 'flex-start' : 'center' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap', paddingTop: expanded ? 6 : 0 }}>
                        프롬프트
                      </span>
                      <textarea
                        value={s.prompt}
                        onChange={e => setStepPrompt(idx, e.target.value)}
                        placeholder={placeholderPrompt}
                        className={`field field-prompt wb-prompt ${expanded ? 'expanded' : 'collapsed'}`}
                        rows={expanded ? 3 : 1}
                        style={expanded ? { flex: 1 } : { flex: 1, resize: 'none', overflow: 'hidden', whiteSpace: 'nowrap', minHeight: 'auto' }}
                        disabled={s.status === 'running'}
                      />
                      <button
                        type="button"
                        className="btn-xs"
                        onClick={() => togglePromptExpanded(s.id)}
                        title={expanded ? '프롬프트 접기' : '프롬프트 펼치기'}
                        style={{ fontSize: 11, padding: '0 6px', flexShrink: 0 }}
                      >{expanded ? '▴' : '▾'}</button>
                    </div>
                  )
                })()}

                {/* KBNL-99: 좌(변환 전 / base) / 우(변환 후 / result) 분할.
                    base 는 resolveBase 가 계산하는 입력값을 read-only 로 표시.
                    버티컬 스택을 피해 한눈에 비교 가능. */}
                {(() => {
                  const baseInfo = resolveBase(sourceSql, steps, idx, s.kind, s.mode, step0)
                  const baseIsYaml = baseInfo.kind === 'yaml'
                  // KBNL-99: 모든 step 의 좌측 ' 변환 전' 패널을 편집 가능하게 한다.
                  // override 가 있으면 그 값을, 없으면 자동 baseInfo.value 표시.
                  // import/upload 는 override 로 채워진다 (SQL/YAML 입력 공통).
                  const baseEditable = true
                  const baseDisplayValue = (s.base_override && s.base_override.length > 0)
                    ? s.base_override
                    : (baseInfo.value || '')
                  const baseExt  = baseIsYaml ? 'yaml' : 'sql'
                  const baseMime = baseIsYaml ? 'text/yaml' : 'application/sql'
                  return (
                    <div className="wb-step-split">
                      {/* ── 좌측: 변환 전 (base) — 모든 step 편집/import/upload/download ─── */}
                      <div className="wb-step-pane wb-step-pane-base">
                        <div className="wb-step-head wb-pane-head">
                          <span className="wb-result-label">변환 전 ({baseIsYaml ? 'YAML' : 'SQL'})</span>
                          <span className="wb-step-meta">
                            {baseDisplayValue.split('\n').length} lines · {baseHint}
                            {s.base_override ? ' · 사용자 편집' : ''}
                          </span>
                          <span className="wb-pane-head-break" />
                          {s.base_override && (
                            <button
                              className="btn-xs"
                              onClick={() => setStepBaseOverride(idx, '')}
                              title="사용자 편집을 지우고 자동 base 로 되돌리기"
                              style={{ fontSize: 11 }}
                            >자동 base</button>
                          )}
                          <CopyButton value={baseDisplayValue} disabled={!baseDisplayValue.trim()} style={{ fontSize: 11 }} />
                          {/* KBNL-107: 변환 전 — 복사 / 파일 업로드 / 다운로드 / GCS Import / GCS Export */}
                          <button
                            className="btn-xs"
                            onClick={() => setImportTarget({ onImported: (content) => setStepBaseOverride(idx, content) })}
                            style={{ fontSize: 11 }}
                            title="GCS 에서 입력 불러오기"
                          >⤵ GCS</button>
                          {SHOW_LOCAL_FILE_IO && (<>
                          <button
                            className="btn-xs"
                            onClick={() => pickLocalFile((text) => setStepBaseOverride(idx, text))}
                            style={{ fontSize: 11 }}
                            title="로컬 파일에서 입력 불러오기"
                          >⤴ 파일</button>
                          <button
                            className="btn-xs"
                            onClick={() => downloadText(ensureExtension(`${queryName || 'workbench'}_step${stepNo}_base`, baseExt), baseDisplayValue, baseMime)}
                            disabled={!baseDisplayValue.trim()}
                            style={{ fontSize: 11 }}
                            title="입력 다운로드"
                          >⤓ 다운로드</button>
                          </>)}
                          <button
                            className="btn-xs"
                            onClick={() => setExportSource({
                              content: baseDisplayValue,
                              defaultFilename: ensureExtension(`${queryName || 'workbench'}_step${stepNo}_base`, baseExt),
                              contentType: baseMime,
                              title: `Step ${stepNo} 변환 전(${baseIsYaml ? 'YAML' : 'SQL'})를 GCS 로 Export`,
                            })}
                            disabled={!baseDisplayValue.trim()}
                            style={{ fontSize: 11 }}
                            title="이 입력을 GCS 로 Export"
                          >⤴ GCS Export</button>
                          <button
                            className="btn-xs"
                            onClick={() => openTypeSave(baseDisplayValue, baseIsYaml)}
                            disabled={!baseDisplayValue.trim()}
                            style={{ fontSize: 11 }}
                            title="이 입력을 type 에 맞게 Firestore 에 저장 (SQL/SV/CV)"
                          >💾 저장</button>
                          <button
                            className="btn-xs"
                            onClick={() => openLoad(baseIsYaml, (c) => setStepBaseOverride(idx, c))}
                            style={{ fontSize: 11 }}
                            title="Firestore 에서 불러와 입력에 채우기 (덮어쓰기)"
                          >📂 불러오기</button>
                        </div>
                        <div className="wb-editor-wrap editor-wrap">
                          <CodeMirror
                            value={baseDisplayValue}
                            onChange={(v) => setStepBaseOverride(idx, v)}
                            extensions={baseIsYaml ? yamlExtensions : sqlExtensions}
                            theme={cmTheme}
                            style={{ height: '100%' }}
                            onAlignPeer={alignPeerToView}
                            basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
                            placeholder={`이 step 의 입력 (${baseIsYaml ? 'YAML' : 'SQL'}). 직접 입력/수정하면 LLM 입력으로 사용. 비우면 자동 base (직전 결과/소스).`}
                          />
                        </div>
                      </div>

                      {/* ── 우측: 변환 후 (result) ───────────────────────── */}
                      <div className="wb-step-pane wb-step-pane-result">
                        <div className="wb-step-head wb-pane-head wb-result-head">
                          <span className="wb-result-label">변환 후 ({isYaml ? 'YAML' : 'SQL'})</span>
                          <span className="wb-step-meta">{resultText.split('\n').length} lines</span>
                          {isYaml && s.result_yaml && (
                            <span className={`sql-type-badge ${typeBadgeClass(detectYamlType(s.result_yaml))}`}>
                              {typeLabel(detectYamlType(s.result_yaml))}
                            </span>
                          )}
                          {/* KBNL-110 정리: '별도 저장' 토글+이름란 제거 — '💾 저장' 으로 대체 */}
                          <span className="wb-pane-head-break" />
                          {/* KBNL-101: 존재하지 않는 테이블/컬럼 검증 */}
                          {(() => {
                            const vs = validateState[s.id]
                            const running = vs?.running
                            return (
                              <button
                                className="btn-xs"
                                onClick={() => validateStep(s)}
                                disabled={!resultText || running}
                                style={{ fontSize: 11 }}
                                title={isYaml ? 'Semantic View 의 테이블/컬럼 존재 검증' : 'SQL 의 테이블/컬럼 존재 검증 (dry-run)'}
                              >{running ? '검증 중…' : '🔎 스키마 검증'}</button>
                            )
                          })()}
                          {isYaml && s.result_yaml && (() => {
                            const t = detectYamlType(s.result_yaml)
                            if (t !== 'single' && t !== 'composite') return null
                            return renderRecommendPicker(s.result_yaml, t, `step-${idx}`, {}, 'xs')
                          })()}
                          <CopyButton value={resultText} disabled={!resultText} style={{ fontSize: 11 }} />
                          {/* KBNL-107: 변환 후 — GCS Import 추가 (파일/다운로드/GCS Export 와 함께 4세트) */}
                          <button
                            className="btn-xs"
                            onClick={() => setImportTarget({ onImported: (content) => {
                              if (isYaml) setStepResultYaml(idx, content)
                              else setStepResultSql(idx, content)
                            } })}
                            style={{ fontSize: 11 }}
                            title="GCS 에서 결과 불러오기 (덮어쓰기)"
                          >⤵ GCS</button>
                          {/* KBNL-99: 변환 후 결과를 로컬 파일에서 덮어쓰기 (KBNL-112: 기본 숨김) */}
                          {SHOW_LOCAL_FILE_IO && (<>
                          <button
                            className="btn-xs"
                            onClick={() => pickLocalFile((text) => {
                              if (isYaml) setStepResultYaml(idx, text)
                              else setStepResultSql(idx, text)
                            })}
                            style={{ fontSize: 11 }}
                            title="로컬 파일에서 결과 불러오기 (덮어쓰기)"
                          >⤴ 파일</button>
                          <button
                            className="btn-xs"
                            onClick={() => {
                              const ext  = isYaml ? 'yaml' : 'sql'
                              const mime = isYaml ? 'text/yaml' : 'application/sql'
                              const fname = ensureExtension(`${queryName || 'workbench'}_step${stepNo}`, ext)
                              downloadText(fname, resultText, mime)
                            }}
                            disabled={!resultText}
                            style={{ fontSize: 11 }}
                            title="결과 다운로드"
                          >⤓ 다운로드</button>
                          </>)}
                          {/* KBNL-99: step 별 GCS Export — 모든 step (yaml/sql) 의 변환 후 결과 지원 */}
                          <button
                            className="btn-xs"
                            onClick={() => {
                              const ext  = isYaml ? 'yaml' : 'sql'
                              const mime = isYaml ? 'text/yaml' : 'application/sql'
                              const stepVName = (s.view_name || '').trim() || `${queryName || 'workbench'}_step${stepNo}`
                              setExportSource({
                                content: resultText,
                                defaultFilename: ensureExtension(stepVName, ext),
                                contentType: mime,
                                title: `Step ${stepNo} (${labelForKind(s.kind)}) 결과를 GCS 로 Export`,
                              })
                            }}
                            disabled={!resultText}
                            style={{ fontSize: 11 }}
                            title="이 step 결과를 GCS 로 Export"
                          >⤴ GCS Export</button>
                          <button
                            className="btn-xs"
                            onClick={() => openTypeSave(resultText, isYaml)}
                            disabled={!resultText}
                            style={{ fontSize: 11 }}
                            title="이 결과를 type 에 맞게 Firestore 에 저장 (SQL/SV/CV)"
                          >💾 저장</button>
                          <button
                            className="btn-xs"
                            onClick={() => openLoad(isYaml, (c) => { if (isYaml) setStepResultYaml(idx, c); else setStepResultSql(idx, c) })}
                            style={{ fontSize: 11 }}
                            title="Firestore 에서 불러와 결과에 채우기 (덮어쓰기)"
                          >📂 불러오기</button>
                        </div>
                        <div className="wb-editor-wrap editor-wrap">
                          {isYaml ? (
                            <CodeMirror
                              value={s.result_yaml}
                              onChange={(v) => setStepResultYaml(idx, v)}
                              extensions={yamlExtensions}
                              theme={cmTheme}
                              style={{ height: '100%' }}
                              onAlignPeer={alignPeerToView}
                              basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
                              placeholder="실행 시 LLM 의 변환 결과 YAML 이 채워집니다. 직접 편집 가능."
                            />
                          ) : (
                            <CodeMirror
                              value={s.result_sql}
                              onChange={(v) => setStepResultSql(idx, v)}
                              extensions={sqlExtensions}
                              theme={cmTheme}
                              style={{ height: '100%' }}
                              onAlignPeer={alignPeerToView}
                              basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true }}
                              placeholder="실행 시 LLM 의 정제 결과 SQL 이 채워집니다. 직접 편집 가능."
                            />
                          )}
                          {s.status === 'running' && (
                            <div className="wb-step-overlay">
                              <div className="spinner" />
                              <div className="wb-step-overlay-text">
                                Step {stepNo} {labelForKind(s.kind)} 중...
                              </div>
                            </div>
                          )}
                        </div>
                        {/* KBNL-101: 검증 결과 패널 */}
                        {(() => {
                          const vr = validateState[s.id]?.result
                          // KBNL-136(feedback 20260618): 성공(✓)은 토스트로 충분 — 중복 패널 제거.
                          // 패널은 실패 시에만 (오류 목록 표시). 토스트는 양쪽 다 유지.
                          if (!vr || vr.valid) return null
                          const probs = [
                            ...(vr.unknown_tables || []).map(t => `테이블 없음: ${t}`),
                            ...(vr.unknown_columns || []).map(c => `컬럼 없음: ${c}`),
                          ]
                          // dry_run 등에서 errors 만 채워진 경우 그대로 사용
                          const errs = probs.length > 0 ? probs : (vr.errors || [])
                          return (
                            <div className="wb-validate-result" style={{
                              position: 'relative',
                              padding: '4px 24px 4px 8px', fontSize: 11,
                              borderTop: '1px solid var(--border)',
                              background: 'rgba(192,57,43,0.08)',
                              color: '#c0392b',
                              maxHeight: 96, overflowY: 'auto',
                            }}>
                              <button
                                className="wb-validate-close"
                                onClick={() => dismissValidation(s.id)}
                                title="검증 결과 닫기"
                                aria-label="검증 결과 닫기"
                                style={{
                                  position: 'absolute', top: 2, right: 4, lineHeight: 1,
                                  border: 'none', background: 'transparent', cursor: 'pointer',
                                  color: 'inherit', fontSize: 13, opacity: 0.7, padding: '0 2px',
                                }}
                              >×</button>
                              <div>
                                <div style={{ fontWeight: 600 }}>✗ 검증 실패 — {errs.length}건 <span style={{ opacity: 0.6 }}>({vr.method})</span></div>
                                <ul style={{ margin: '2px 0 0', paddingLeft: 16 }}>
                                  {errs.slice(0, 20).map((m, i) => <li key={i}>{m}</li>)}
                                </ul>
                              </div>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )
          })}

          {/* KBNL-99: 하단 'Step 추가' 패널 제거 — 우측 STEPS 네비의 각 + 갭으로
              통합 (중간 삽입 + 맨 끝 추가 모두 거기서). 편집창 공간 확보. */}
        </div>
      </div>

      {/* ── KBNL-99: 우측 STEPS 네비 — 본문은 활성 step 만 보임, 여기서 전환만.
           각 항목 사이에 + 버튼으로 그 위치에 step 삽입 가능. */}
      <aside className="wb-stepnav" aria-label="Step 네비게이션">
        <div className="wb-stepnav-title" title="Step 네비게이션">STEPS</div>
        {/* Step 0 — KBNL-103: 다른 step 과 동일한 변환 step 으로 통합 표기
            (소스 입력 + 변환 결과). 네비 항목도 TypeFlowBadge·상태·편집 점을 동일하게. */}
        {(() => {
          const cls = [
            'wb-stepnav-item',
            `wb-stepnav-kind-${step0.kind}`,
            isYamlKind(step0.kind) ? 'is-final' : '',
            step0.status === 'running' ? 'is-running' : '',
            step0.status === 'error'   ? 'is-error'   : '',
            step0.status === 'done'    ? 'is-done'    : '',
            activeStepKey === 'step-0' ? 'is-active' : '',
          ].filter(Boolean).join(' ')
          return (
            <button
              type="button"
              className={cls}
              onClick={() => setActiveStepKey('step-0')}
              title={`Step 0 — ${labelForKind(step0.kind)}${step0.edited ? ' (편집됨)' : ''}`}
            >
              <span className="wb-stepnav-num">0</span>
              <TypeFlowBadge kind={step0.kind} size="xs" />
              {step0.edited && <span className="wb-stepnav-dot" title="편집됨" />}
            </button>
          )
        })()}
        {/* Step 0 직후 (= Step 1 자리) 삽입 갭 */}
        <WbStepInsertBetween
          insertAt={0}
          steps={steps}
          onInsert={insertStep}
          compact
        />
        {steps.map((s, idx) => {
          const stepNo = idx + 1
          const cls = [
            'wb-stepnav-item',
            `wb-stepnav-kind-${s.kind}`,
            isYamlKind(s.kind) ? 'is-final' : '',
            s.status === 'running' ? 'is-running' : '',
            s.status === 'error'   ? 'is-error'   : '',
            s.status === 'done'    ? 'is-done'    : '',
            activeStepKey === `step-${s.id}` ? 'is-active' : '',
          ].filter(Boolean).join(' ')
          return (
            <Fragment key={s.id}>
              <div className="wb-stepnav-row" style={{ display: 'flex', alignItems: 'stretch', gap: 2 }}>
                <button
                  type="button"
                  className={cls}
                  onClick={() => setActiveStepKey(`step-${s.id}`)}
                  title={`Step ${stepNo} — ${labelForKind(s.kind)}${s.edited ? ' (편집됨)' : ''}`}
                  style={{ flex: 1 }}
                >
                  <span className="wb-stepnav-num">{stepNo}</span>
                  <TypeFlowBadge kind={s.kind} size="xs" />
                  {s.edited && <span className="wb-stepnav-dot" title="편집됨" />}
                </button>
                {/* KBNL-99: 네비에서 직접 step 삭제 (cascade 검증은 removeStep 안에서) */}
                <button
                  type="button"
                  className="wb-stepnav-del"
                  onClick={() => removeStep(idx)}
                  disabled={s.status === 'running'}
                  title={`Step ${stepNo} 삭제`}
                  style={{ fontSize: 11, padding: '0 4px', color: 'var(--danger, #c0392b)', background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}
                >×</button>
              </div>
              {/* 이 step 직후 삽입 갭 */}
              <WbStepInsertBetween
                insertAt={idx + 1}
                steps={steps}
                onInsert={insertStep}
                compact
              />
            </Fragment>
          )
        })}
      </aside>

      {/* ── 동명 레코드 덮어쓰기 다이얼로그 ─────────────────────────── */}
      {overwriteDialog && (
        <div
          className="explore-copy-overlay"
          style={{ position: 'fixed', zIndex: 1500 }}
          onClick={() => !saving && setOverwriteDialog(null)}
        >
          <div className="explore-copy-dialog" onClick={e => e.stopPropagation()}>
            <h3>이미 존재하는 항목 덮어쓰기</h3>
            <div className="form-hint" style={{ marginBottom: 8 }}>
              다음 항목이 이미 존재합니다. 저장하면 기존 내용을 덮어씁니다.
            </div>
            <ul style={{ margin: '6px 0 4px 18px', padding: 0, fontSize: 13 }}>
              {overwriteDialog.overwrites.map((o, i) => (
                <li key={i} style={{ margin: '2px 0' }}>{o}</li>
              ))}
            </ul>
            <div className="explore-copy-actions">
              <button className="btn-secondary" onClick={() => setOverwriteDialog(null)} disabled={saving}>
                취소
              </button>
              <button className="btn-primary" onClick={confirmOverwrite} disabled={saving}>
                덮어쓰기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 복제 다이얼로그 ───────────────────────────────────────────── */}
      <DuplicateDialog
        open={!!duplicateDialog}
        defaultName={duplicateDialog?.defaultName || ''}
        busy={duplicating}
        title="SQL Workbench 복제"
        hint="Workbench step 들과 연결된 Semantic View / Composite View 까지 같은 이름 규칙으로 함께 복제됩니다."
        onConfirm={handleDuplicate}
        onCancel={() => { if (!duplicating) setDuplicateDialog(null) }}
      />

      {/* KBNL-108: 패널 type별 Firestore 저장 — 이름 입력 팝업 (동명 충돌은 confirm 으로 덮어쓰기/취소) */}
      <DuplicateDialog
        open={!!saveAsDialog}
        defaultName={saveAsDialog?.defaultName || ''}
        busy={saveAsBusy}
        title={
          saveAsDialog?.kind === 'cv' ? 'Composite View 로 저장'
          : saveAsDialog?.kind === 'sv' ? 'Semantic View 로 저장'
          : 'SQL 로 저장'
        }
        nameLabel="저장 이름"
        hint="Firestore 에 저장합니다. 같은 이름이 있으면 덮어쓰기/취소(이름 변경)를 묻습니다."
        confirmLabel="저장"
        busyLabel="저장 중..."
        onConfirm={doTypeSave}
        onCancel={() => { if (!saveAsBusy) setSaveAsDialog(null) }}
      />

      {/* KBNL-111: 📂 불러오기 — Firestore 검색 목록 팝업 → 선택 시 내용 주입 */}
      {loadPicker && (() => {
        const all = loadPicker.isYaml
          ? [
              ...savedMetricViews.map(v => ({ ...v, _type: 'sv' })),
              ...savedCompositeViews.map(v => ({ ...v, _type: 'cv' })),
            ]
          : savedQueries.map(v => ({ ...v, _type: 'sql' }))
        const q = loadPickerSearch.trim().toLowerCase()
        const items = q ? all.filter(v => (v.name || '').toLowerCase().includes(q)) : all
        const typeLabelOf = (t) => t === 'sv' ? 'SV' : t === 'cv' ? 'CV' : 'SQL'
        return (
          <div className="explore-copy-overlay" style={{ position: 'fixed', zIndex: 1500 }} onClick={() => !loadPickerBusy && setLoadPicker(null)}>
            <div className="explore-copy-dialog" onClick={e => e.stopPropagation()} style={{ width: 460, maxWidth: '92vw' }}>
              <h3>📂 불러오기 — {loadPicker.isYaml ? 'Semantic / Composite View' : 'SQL 쿼리'}</h3>
              <div className="form-hint" style={{ marginBottom: 6 }}>
                Firestore 에서 선택하면 내용이 현재 칸에 그대로 채워집니다(덮어쓰기).
              </div>
              <input
                className="field"
                autoFocus
                value={loadPickerSearch}
                onChange={e => setLoadPickerSearch(e.target.value)}
                placeholder="이름으로 검색"
                style={{ marginBottom: 8 }}
              />
              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                {items.length === 0 ? (
                  <div className="std-list-empty" style={{ padding: 16 }}>항목이 없습니다.</div>
                ) : items.map((item, i) => (
                  <div
                    key={`${item._type}:${item.owner_user_id || ''}:${item.name}:${i}`}
                    onClick={() => { if (!loadPickerBusy) doLoad(item) }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                      cursor: loadPickerBusy ? 'wait' : 'pointer',
                      borderBottom: '1px solid var(--border)',
                    }}
                    title={item.description || item.name}
                  >
                    <span className="sql-type-badge" style={{ flexShrink: 0 }}>{typeLabelOf(item._type)}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                    {item.is_mine === false && (
                      <span className="wb-step-meta" style={{ flexShrink: 0 }}>{item.owner_name || item.owner_user_id || '타인'}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="explore-copy-actions" style={{ marginTop: 10 }}>
                <button className="btn-secondary" onClick={() => setLoadPicker(null)} disabled={loadPickerBusy}>닫기</button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* KBNL-99 §7: GCS Export 다이얼로그 — step 별 / 헤더 단축 공통.
          exportSource 가 set 되면 그 source 의 content 와 filename 으로 다이얼로그.
          경로는 storageSettings.gcs_path 가 있으면 기본값으로. */}
      <GcsExportDialog
        open={!!exportSource}
        onClose={() => setExportSource(null)}
        defaultPath={storageSettings?.gcs_path || 'gs://'}
        defaultFilename={exportSource?.defaultFilename || ''}
        defaultExt={(exportSource?.contentType || '').includes('yaml') ? 'yaml' : 'sql'}
        content={exportSource?.content || ''}
        contentType={exportSource?.contentType || 'text/plain'}
        showToast={showToast}
        title={exportSource?.title || 'GCS Export'}
      />

      {/* KBNL-99: GCS Import 다이얼로그 — 소스/변환 전 입력을 GCS 파일에서 로드 */}
      <GcsImportDialog
        open={!!importTarget}
        onClose={() => setImportTarget(null)}
        defaultPath={storageSettings?.gcs_path || 'gs://'}
        onImported={(content) => { importTarget?.onImported?.(content); setImportTarget(null) }}
        showToast={showToast}
        title="GCS 에서 불러오기"
      />

      {/* ── KBNL-74: SV 추천 모달 ──────────────────────────────────────────
           후보 행 클릭 시 해당 SV 화면(SV 탭)으로 이동. */}
      {/* 랭킹 품질: 변환 결과 SV(recommendSeed)가 있으면 seed_sv + override_yaml
          로 검색 — SV 탭과 동일 품질 (vector 질의 풍부 + identifier 채널 활성 +
          동명 저장본 exclude). 없으면 mode='sql' 로 sourceSql 기반 (변환 전 fallback). */}
      <SvRecommendDialog
        open={recommendOpen}
        onClose={() => setRecommendOpen(false)}
        mode={recommendSeed ? 'seed_sv' : 'sql'}
        sqlText={sourceSql}
        seedSvName={recommendSeed?.name || ''}
        seedSvOverrideYaml={recommendSeed?.yaml || ''}
        seedSvOverrideDescription={recommendSeed?.description || ''}
        storageSettings={storageSettings}
        showToast={showToast}
        editorTheme={editorTheme}
        onSelect={(c) => {
          if (c?.name && onNavigateToView) {
            // KBNL-141(리뷰): owner 전달 — 같은 이름 다른 owner 후보 정확 로드.
            onNavigateToView(c.name, c.owner_user_id || c.user_id || '')
          } else if (c?.name) {
            showToast?.('warning', '이 화면에서는 SV 화면으로 이동할 수 없습니다.')
          }
        }}
        title={
          recommendSeed
            ? `'${recommendSeed.name}' 와(과) 유사한 Semantic View`
            : '유사 SV 찾기 — Workbench 소스 SQL 기반'
        }
      />
    </div>
  )
}
